import React, { Component } from 'react';
import { withRouter } from 'react-router-dom';
import { compose } from '../../utils/compose';
import { withFirebase } from '../Firebase';
import { withAdminAuthorization } from '../Session';
import * as ROUTES from '../../constants/routes';
import mandatesSeedData from '../../modules/exclusiveMandates/mandatesData';
import { supportTicketGet, supportTicketsList, supportTicketUpdateStatus } from '../../lib/support/supportApi';
import {
  adminNotificationsHealth,
  adminNotificationsHistory,
  adminNotificationsSend,
  adminNotificationsTemplates,
} from '../../lib/notifications/adminNotificationsApi';
import {
  buildNotificationDesignPayload,
  resolveNotificationDesign,
  toSafeHttpUrl,
} from '../../lib/notifications/design';
import './AdminDashboard.css';

const DEFAULT_NOTIFICATION_CREATIVE_FIELDS = {
  preset: 'classic',
  tone: 'neutral',
  buttonStyle: 'solid',
  textAlign: 'left',
  contentAlign: 'start',
  eyebrow: '',
  badge: '',
  subtitle: '',
  footer: '',
  icon: '',
  accentColor: '',
  backgroundColor: '',
  textColor: '',
  bodyColor: '',
  ctaLabel: '',
  ctaUrl: '',
  secondaryCtaLabel: '',
  secondaryCtaUrl: '',
  imageUrl: '',
  imagePosition: 'left',
  imageFit: 'cover',
  autoDismissSec: '7',
};

const DEFAULT_NOTIFICATION_COMPOSER = {
  category: 'SYSTEM',
  channel: 'in_app',
  priority: 'normal',
  targetMode: 'all',
  userIdsRaw: '',
  franchiseIdsRaw: '',
  templateName: '',
  title: '',
  body: '',
  scheduleAt: '',
  dataJson: '',
  ...DEFAULT_NOTIFICATION_CREATIVE_FIELDS,
};

class AdminDashboardBase extends Component {
  constructor(props) {
    super(props);
    this.state = {
      franchises: [],
      disapprovedFranchises: [],
      agreements: [], // Franchise agreements
      paymentCompletedUsers: [], // Users who completed payment
      onboardingData: [], // Franchise onboarding form data
      onboardingRequests: [], // Preferred email onboarding requests
      loading: true,
      filter: 'all', // 'all', 'pending', 'pending_setup', 'active', 'disapproved'
      searchQuery: '',
      currentPhase: 'approval', // 'approval', 'agreement', 'paymentSuccessful', 'signedPdfs', 'downloadedSignedPdfs', 'customers', 'franchiseeCard', 'onboarding'
      deleteModalOpen: false,
      agreementToDelete: null,
      deletingAgreement: false,
      customerDetailsModal: false,
      selectedCustomer: null,
      cardPreviewOpen: false,
      cardPreviewAgreement: null,
      cardPreviewHovered: false,
      assignedEmailEdits: {},
      savingAssignedEmail: {},
      assignedEmailEditMode: {},
      mandates: [],
      mandateRegistrations: [],
      mandatesLoading: true,
      mandatesSeeded: false,
      selectedMandateId: null,
      academySessions: [],
      academySessionsLoading: true,
      newSession: {
        title: '',
        description: '',
        startTime: '',
        endTime: '',
        meetLink: '',
      },
      academyEvents: [],
      academyEventsLoading: true,
      newEvent: {
        title: '',
        description: '',
        startTime: '',
        endTime: '',
        location: '',
      },
      supportTickets: [],
      supportLoading: true,
      supportError: '',
      supportSelectedId: null,
      supportMessages: [],
      supportMessagesLoading: false,
      supportRefreshTimer: null,
      supportResolving: {},
      supportStatusFilter: 'all',
      supportPriorityFilter: 'all',
      supportSortBy: 'latest_activity',
      supportSearchQuery: '',
      notificationsHealth: null,
      notificationCampaigns: [],
      notificationTemplates: [],
      notificationsLoading: true,
      notificationsError: '',
      notificationsRefreshTimer: null,
      notificationsSending: false,
      notificationsStatus: '',
      notificationsStatusType: '',
      notificationComposer: { ...DEFAULT_NOTIFICATION_COMPOSER },
    };
  }

  componentDidMount() {
    // Debug: Check current user auth info
    const currentUser = this.props.firebase.auth.currentUser;
    if (currentUser) {
      console.log('ðŸ” Current Admin User Info:');
      console.log('- UID:', currentUser.uid);
      console.log('- Email:', currentUser.email);
      console.log('- Email Verified:', currentUser.emailVerified);

      // Get the ID token to see what's in it
      currentUser.getIdTokenResult().then((idTokenResult) => {
        console.log('ðŸŽ« ID Token Claims:');
        console.log('- Email in token:', idTokenResult.claims.email);
        console.log('- All claims:', idTokenResult.claims);
      }).catch((error) => {
        console.error('Error getting token:', error);
      });
    }

    // Listen to active/pending franchises
    this.unsubscribeFranchises = this.props.firebase.db
      .collection('franchises')
      .orderBy('createdAt', 'desc')
      .onSnapshot(async (snapshot) => {
        const franchises = [];

        // Load all franchises with their onboarding data
        for (const doc of snapshot.docs) {
          const franchiseData = { id: doc.id, ...doc.data() };

          // Load onboarding data to get personal details
          try {
            const onboardingDoc = await this.props.firebase.db
              .collection('franchiseOnboarding')
              .doc(doc.id)
              .get();

            if (onboardingDoc.exists) {
              const onboardingData = onboardingDoc.data();
              franchiseData.personalDetails = onboardingData.personalDetails;
              franchiseData.teamMembers = onboardingData.teamMembers;
              franchiseData.territory = onboardingData.territory;
            }
          } catch (error) {
            console.error('Error loading onboarding data:', error);
          }

          franchises.push(franchiseData);
        }

        this.setState({ franchises, loading: false });
      }, (error) => {
        console.error('Error loading franchises:', error);
        this.setState({ loading: false });
      });

    // Listen to disapproved franchises
    this.unsubscribeDisapproved = this.props.firebase.db
      .collection('disapprovedFranchises')
      .orderBy('disapprovedAt', 'desc')
      .onSnapshot((snapshot) => {
        const disapprovedFranchises = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
          status: 'disapproved',
        }));

        this.setState({ disapprovedFranchises });
      }, (error) => {
        console.error('Error loading disapproved franchises:', error);
      });

    // Listen to franchise agreements
    this.unsubscribeAgreements = this.props.firebase.db
      .collection('franchiseAgreements')
      .orderBy('createdAt', 'desc')
      .onSnapshot((snapshot) => {
        const agreements = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
        }));

        console.log('âœ… Agreements loaded:', agreements.length, 'agreements');
        if (agreements.length > 0) {
          console.log('Latest agreement:', {
            id: agreements[0].id,
            status: agreements[0].status,
            userEmail: agreements[0].userEmail,
            createdAt: agreements[0].createdAt,
          });
        }

        this.setState({ agreements });
      }, (error) => {
        console.error('âŒ Error loading agreements:', error);
        console.error('Error details:', error.message);

        // Fallback: Try without orderBy if index doesn't exist
        console.log('âš ï¸ Attempting to load agreements without orderBy...');
        this.props.firebase.db
          .collection('franchiseAgreements')
          .get()
          .then((snapshot) => {
            const agreements = snapshot.docs.map(doc => ({
              id: doc.id,
              ...doc.data(),
            }));
            console.log('âœ… Loaded', agreements.length, 'agreements (fallback)');
            this.setState({ agreements });
          })
          .catch((err) => {
            console.error('âŒ Fallback also failed:', err);
          });
      });

    // Listen to payment completed users from franchiseOwners collection
    this.unsubscribePaymentCompleted = this.props.firebase.db
      .collection('franchiseOwners')
      .where('status', '==', 'payment_completed')
      .onSnapshot((snapshot) => {
        const paymentCompletedUsers = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
        }));

        console.log('âœ… Payment completed users loaded:', paymentCompletedUsers.length);
        this.setState({ paymentCompletedUsers });
      }, (error) => {
        console.error('âŒ Error loading payment completed users:', error);
        // Fallback without where if index doesn't exist
        this.props.firebase.db
          .collection('franchiseOwners')
          .get()
          .then((snapshot) => {
            const paymentCompletedUsers = snapshot.docs
              .map(doc => ({
                id: doc.id,
                ...doc.data(),
              }))
              .filter(user => user.status === 'payment_completed');
            console.log('âœ… Loaded', paymentCompletedUsers.length, 'payment successful users (fallback)');
            this.setState({ paymentCompletedUsers });
          })
          .catch((err) => {
            console.error('âŒ Fallback also failed:', err);
          });
      });

    // Listen to franchise onboarding data
    this.unsubscribeOnboarding = this.props.firebase.db
      .collection('franchiseOnboarding')
      .onSnapshot((snapshot) => {
        const onboardingData = snapshot.docs.map(doc => {
          const data = doc.data();
          console.log('dY", Onboarding doc:', doc.id, 'userId:', data.userId, 'franchiseId:', data.franchiseId);
          return {
            id: doc.id,
            franchiseId: doc.id, // Document ID is the franchise ID
            ...data,
          };
        });

        console.log('?o. Onboarding data loaded:', onboardingData.length, onboardingData);
        this.setState({ onboardingData });
      }, (error) => {
        console.error('??O Error loading onboarding data:', error);
        console.error('Full error:', error.message, error.code);
      });

    // Listen to preferred email onboarding requests
    this.unsubscribeOnboardingRequests = this.props.firebase.db
      .collection('onboardingRequests')
      .orderBy('submittedAt', 'desc')
      .onSnapshot((snapshot) => {
        const onboardingRequests = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
        }));
        this.setState({ onboardingRequests });
      }, (error) => {
        console.error('Error loading onboarding requests:', error);
        // Fallback without orderBy
        this.props.firebase.db
          .collection('onboardingRequests')
          .get()
          .then((snapshot) => {
            const onboardingRequests = snapshot.docs.map(doc => ({
              id: doc.id,
              ...doc.data(),
            }));
            this.setState({ onboardingRequests });
          })
          .catch((err) => {
            console.error('Fallback onboarding requests failed:', err);
          });
      });

    // Mandates (admin-only)
    this.seedMandates();
    this.setupMandatesListeners();

    // Academy sessions (admin-only)
    this.setupAcademySessionsListener();

    // Academy events (admin-only)
    this.setupAcademyEventsListener();

    // Support tickets (admin-only)
    this.setupSupportTicketsListener();

    // Notification center (admin-only)
    this.setupAdminNotificationsListener();
  }

  componentWillUnmount() {
    if (this.unsubscribeFranchises) {
      this.unsubscribeFranchises();
    }
    if (this.unsubscribeDisapproved) {
      this.unsubscribeDisapproved();
    }
    if (this.unsubscribeAgreements) {
      this.unsubscribeAgreements();
    }
    if (this.unsubscribePaymentCompleted) {
      this.unsubscribePaymentCompleted();
    }
    if (this.unsubscribeOnboarding) {
      this.unsubscribeOnboarding();
    }
    if (this.unsubscribeOnboardingRequests) {
      this.unsubscribeOnboardingRequests();
    }
    if (this.unsubscribeMandates) {
      this.unsubscribeMandates();
    }
    if (this.unsubscribeMandateRegistrations) {
      this.unsubscribeMandateRegistrations();
    }
    if (this.unsubscribeAcademySessions) {
      this.unsubscribeAcademySessions();
    }
    if (this.unsubscribeAcademyEvents) {
      this.unsubscribeAcademyEvents();
    }
    if (this.unsubscribeSupportTickets) {
      this.unsubscribeSupportTickets();
    }
    if (this.state.supportRefreshTimer) {
      clearInterval(this.state.supportRefreshTimer);
    }
    if (this.state.notificationsRefreshTimer) {
      clearInterval(this.state.notificationsRefreshTimer);
    }
  }

  setupSupportTicketsListener = () => {
    const firebase = this.props.firebase;
    if (!firebase) {
      this.setState({ supportLoading: false });
      return;
    }

    const loadTickets = async () => {
      try {
        this.setState({ supportError: '' });
        const data = await supportTicketsList(firebase, { all: 1, limit: 200 });
        const supportTickets = (data?.items || []).map((ticket) => ({
          id: ticket.id,
          ...ticket,
        }));
        this.setState({ supportTickets, supportLoading: false, supportError: '' });
      } catch (error) {
        console.error('Error loading support tickets:', error);
        this.setState({
          supportLoading: false,
          supportError: error?.message || 'Failed to load support tickets.',
        });
      }
    };

    loadTickets();
    const supportRefreshTimer = setInterval(loadTickets, 60000);
    this.setState({ supportRefreshTimer });
  };

  loadSupportMessages = async (ticketId) => {
    const firebase = this.props.firebase;
    if (!firebase || !ticketId) return;
    this.setState({ supportMessagesLoading: true });
    try {
      const ticket = this.state.supportTickets.find((item) => item.id === ticketId);
      const data = await supportTicketGet(firebase, ticketId, ticket?.uid ? { uid: ticket.uid } : {});
      const supportMessages = data?.messages || [];
      this.setState({ supportMessages, supportMessagesLoading: false });
    } catch (error) {
      console.error('Error loading support messages:', error);
      this.setState({ supportMessagesLoading: false });
    }
  };

  handleSelectSupportTicket = (ticketId) => {
    this.setState(
      { supportSelectedId: ticketId, supportMessages: [] },
      () => this.loadSupportMessages(ticketId),
    );
  };

  handleResolveSupportTicket = async (ticket) => {
    if (!ticket?.id || !ticket?.uid) return;
    const { firebase } = this.props;
    this.setState((prev) => ({
      supportResolving: { ...prev.supportResolving, [ticket.id]: true },
    }));
    try {
      const result = await supportTicketUpdateStatus(firebase, ticket.id, {
        uid: ticket.uid,
        status: 'resolved',
      });
      const updatedTicket = result?.ticket || { ...ticket, status: 'resolved' };
      this.setState((prev) => ({
        supportTickets: prev.supportTickets.map((item) =>
          item.id === ticket.id ? { ...item, ...updatedTicket } : item,
        ),
        supportResolving: { ...prev.supportResolving, [ticket.id]: false },
      }));
    } catch (error) {
      console.error('Error resolving support ticket:', error);
      this.setState((prev) => ({
        supportResolving: { ...prev.supportResolving, [ticket.id]: false },
      }));
      alert(error?.message || 'Failed to resolve ticket.');
    }
  };

  loadAdminNotifications = async () => {
    const firebase = this.props.firebase;
    if (!firebase) {
      this.setState({
        notificationsLoading: false,
        notificationCampaigns: [],
        notificationTemplates: [],
      });
      return;
    }

    try {
      const [healthData, historyData, templatesData] = await Promise.all([
        adminNotificationsHealth(firebase),
        adminNotificationsHistory(firebase, { limit: 50 }),
        adminNotificationsTemplates(firebase, { active: 1 }),
      ]);

      this.setState({
        notificationsHealth: healthData || null,
        notificationCampaigns: historyData?.items || [],
        notificationTemplates: templatesData?.items || [],
        notificationsLoading: false,
        notificationsError: '',
      });
    } catch (error) {
      console.error('Error loading admin notifications:', error);
      this.setState({
        notificationsLoading: false,
        notificationsError: error?.message || 'Unable to load notifications.',
      });
    }
  };

  setupAdminNotificationsListener = () => {
    this.loadAdminNotifications();
    const notificationsRefreshTimer = setInterval(this.loadAdminNotifications, 60000);
    this.setState({ notificationsRefreshTimer });
  };

  handleNotificationComposerChange = (field, value) => {
    this.setState((prev) => ({
      notificationComposer: {
        ...prev.notificationComposer,
        [field]: value,
      },
      notificationsStatus: '',
      notificationsStatusType: '',
    }));
  };

  parseCsvList = (value) =>
    String(value || '')
      .split(',')
      .map((item) => item.trim())
      .filter(Boolean);

  resetNotificationCreativeFields = () => {
    this.setState((prev) => ({
      notificationComposer: {
        ...prev.notificationComposer,
        ...DEFAULT_NOTIFICATION_CREATIVE_FIELDS,
      },
      notificationsStatus: '',
      notificationsStatusType: '',
    }));
  };

  buildNotificationDesignFromComposer = (composer = {}) => {
    const raw = {
      ...composer,
      autoDismissSec: String(composer.autoDismissSec || '').trim(),
    };

    const built = buildNotificationDesignPayload(raw);
    const autoDismissSecRaw = String(composer.autoDismissSec || '').trim();
    if (autoDismissSecRaw) {
      const parsedAutoDismiss = Number(autoDismissSecRaw);
      if (Number.isFinite(parsedAutoDismiss)) {
        built.autoDismissSec = Math.max(3, Math.min(60, Math.round(parsedAutoDismiss)));
      }
    }

    return built;
  };

  getNotificationPreviewModel = () => {
    const composer = this.state.notificationComposer || DEFAULT_NOTIFICATION_COMPOSER;
    const designPayload = this.buildNotificationDesignFromComposer(composer);
    const design = resolveNotificationDesign({
      data: {
        design: designPayload,
      },
    });

    const title = String(composer.title || '').trim() || 'Preview title';
    const body =
      String(composer.body || '').trim() ||
      'Write a creative message with formatting, CTA, media, and personalized data.';

    return {
      title,
      body,
      design,
    };
  };

  sendNotificationCampaign = async () => {
    const { firebase } = this.props;
    const { notificationComposer } = this.state;

    if (!firebase) return;

    const category = String(notificationComposer.category || '').trim();
    const channel = String(notificationComposer.channel || 'in_app').trim();
    const priority = String(notificationComposer.priority || 'normal').trim();
    const targetMode = String(notificationComposer.targetMode || 'all').trim();
    const templateName = String(notificationComposer.templateName || '').trim();
    const title = String(notificationComposer.title || '').trim();
    const body = String(notificationComposer.body || '').trim();
    const scheduleAt = String(notificationComposer.scheduleAt || '').trim();

    if (!category) {
      this.setState({
        notificationsStatus: 'Category is required.',
        notificationsStatusType: 'error',
      });
      return;
    }

    if (!templateName && (!title || !body)) {
      this.setState({
        notificationsStatus: 'Add a template or provide both title and body.',
        notificationsStatusType: 'error',
      });
      return;
    }

    let data = {};
    if (String(notificationComposer.dataJson || '').trim()) {
      try {
        data = JSON.parse(notificationComposer.dataJson);
      } catch (_error) {
        this.setState({
          notificationsStatus: 'Data JSON is invalid.',
          notificationsStatusType: 'error',
        });
        return;
      }
    }

    const designPayload = this.buildNotificationDesignFromComposer(notificationComposer);
    if (notificationComposer.imageUrl && !toSafeHttpUrl(notificationComposer.imageUrl)) {
      this.setState({
        notificationsStatus: 'Image URL is invalid. Use a direct https image link.',
        notificationsStatusType: 'error',
      });
      return;
    }

    const existingDesign =
      data?.design && typeof data.design === 'object' ? data.design : {};
    data = {
      ...data,
      design: {
        ...existingDesign,
        ...designPayload,
      },
    };

    const target = { all: targetMode === 'all' };
    if (targetMode === 'userIds') {
      target.userIds = this.parseCsvList(notificationComposer.userIdsRaw);
      if (!target.userIds.length) {
        this.setState({
          notificationsStatus: 'Enter at least one User ID.',
          notificationsStatusType: 'error',
        });
        return;
      }
    } else if (targetMode === 'franchiseIds') {
      target.franchiseIds = this.parseCsvList(notificationComposer.franchiseIdsRaw);
      if (!target.franchiseIds.length) {
        this.setState({
          notificationsStatus: 'Enter at least one Franchise ID.',
          notificationsStatusType: 'error',
        });
        return;
      }
    }

    const payload = {
      target,
      notification: {
        category,
        channel,
        priority,
        ...(templateName ? { templateName } : {}),
        ...(title ? { title } : {}),
        ...(body ? { body } : {}),
        ...(scheduleAt ? { scheduleAt: new Date(scheduleAt).toISOString() } : {}),
        data,
      },
    };

    this.setState({
      notificationsSending: true,
      notificationsStatus: '',
      notificationsStatusType: '',
    });

    try {
      const result = await adminNotificationsSend(firebase, payload, {
        idempotencyKey: `admin-send-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
      });
      this.setState({
        notificationsSending: false,
        notificationsStatus: `Campaign sent. Created: ${result?.created ?? 0}, skipped: ${result?.skipped ?? 0}.`,
        notificationsStatusType: 'success',
      });
      await this.loadAdminNotifications();
    } catch (error) {
      this.setState({
        notificationsSending: false,
        notificationsStatus: error?.message || 'Failed to send campaign.',
        notificationsStatusType: 'error',
      });
    }
  };

  seedMandates = async () => {
    const { mandatesSeeded } = this.state;
    const firebase = this.props.firebase;

    if (mandatesSeeded || !firebase?.db?.collection) {
      return;
    }

    try {
      const mandatesRef = firebase.db.collection('exclusive_mandates');
      const batch = firebase.db.batch();
      const timestamp = firebase.fieldValue?.serverTimestamp
        ? firebase.fieldValue.serverTimestamp()
        : new Date();

      mandatesSeedData.forEach((mandate) => {
        const docRef = mandatesRef.doc(mandate.id);
        batch.set(
          docRef,
          {
            ...mandate,
            updatedAt: timestamp,
          },
          { merge: true },
        );
      });

      await batch.commit();
      this.setState({ mandatesSeeded: true });
    } catch (error) {
      console.error('Failed to seed mandates:', error);
    }
  };

  setupMandatesListeners = () => {
    const firebase = this.props.firebase;
    if (!firebase?.db?.collection) {
      this.setState({ mandatesLoading: false });
      return;
    }

    this.unsubscribeMandates = firebase.db
      .collection('exclusive_mandates')
      .onSnapshot(
        (snapshot) => {
          const mandates = snapshot.docs.map((doc) => ({
            id: doc.id,
            ...doc.data(),
          }));
          this.setState({ mandates, mandatesLoading: false });
        },
        (error) => {
          console.error('Error loading mandates:', error);
          this.setState({ mandatesLoading: false });
        },
      );

    this.unsubscribeMandateRegistrations = firebase.db
      .collection('exclusive_mandate_registrations')
      .onSnapshot(
        (snapshot) => {
          const mandateRegistrations = snapshot.docs.map((doc) => ({
            id: doc.id,
            ...doc.data(),
          }));
          this.setState({ mandateRegistrations });
        },
        (error) => {
          console.error('Error loading mandate registrations:', error);
        },
      );
  };

  setupAcademySessionsListener = () => {
    if (!this.props.firebase?.db?.collection) {
      this.setState({ academySessionsLoading: false });
      return;
    }

    this.unsubscribeAcademySessions = this.props.firebase.db
      .collection('academySessions')
      .orderBy('startTime', 'asc')
      .onSnapshot(
        (snapshot) => {
          const academySessions = snapshot.docs.map((doc) => ({
            id: doc.id,
            ...doc.data(),
          }));
          this.setState({ academySessions, academySessionsLoading: false });
        },
        () => {
          this.setState({ academySessionsLoading: false });
        },
      );
  };

  setupAcademyEventsListener = () => {
    if (!this.props.firebase?.db?.collection) {
      this.setState({ academyEventsLoading: false });
      return;
    }

    this.unsubscribeAcademyEvents = this.props.firebase.db
      .collection('academyEvents')
      .orderBy('startTime', 'asc')
      .onSnapshot(
        (snapshot) => {
          const academyEvents = snapshot.docs.map((doc) => ({
            id: doc.id,
            ...doc.data(),
          }));
          this.setState({ academyEvents, academyEventsLoading: false });
        },
        () => {
          this.setState({ academyEventsLoading: false });
        },
      );
  };

  handleEventFieldChange = (field, value) => {
    this.setState((prev) => ({
      newEvent: {
        ...prev.newEvent,
        [field]: value,
      },
    }));
  };

  createAcademyEvent = async () => {
    const { title, description, startTime, endTime, location } = this.state.newEvent;
    if (!title || !startTime || !endTime) {
      alert('Title, start time, and end time are required.');
      return;
    }
    try {
      const payload = {
        title,
        description: description || '',
        startTime: new Date(startTime),
        endTime: new Date(endTime),
        location: location || '',
        createdAt: this.props.firebase.fieldValue?.serverTimestamp
          ? this.props.firebase.fieldValue.serverTimestamp()
          : new Date(),
      };
      await this.props.firebase.db.collection('academyEvents').add(payload);
      this.setState({
        newEvent: {
          title: '',
          description: '',
          startTime: '',
          endTime: '',
          location: '',
        },
      });
    } catch (error) {
      console.error('Failed to create event:', error);
      alert('Failed to create event. Please try again.');
    }
  };

  deleteAcademyEvent = async (eventId) => {
    if (!eventId) return;
    const confirmDelete = window.confirm('Delete this event?');
    if (!confirmDelete) return;
    try {
      await this.props.firebase.db.collection('academyEvents').doc(eventId).delete();
    } catch (error) {
      console.error('Failed to delete event:', error);
      alert('Failed to delete event. Please try again.');
    }
  };

  handleSessionFieldChange = (field, value) => {
    this.setState((prev) => ({
      newSession: {
        ...prev.newSession,
        [field]: value,
      },
    }));
  };

  createAcademySession = async () => {
    const { title, description, startTime, endTime, meetLink } = this.state.newSession;
    if (!title || !startTime || !endTime) {
      alert('Title, start time, and end time are required.');
      return;
    }
    try {
      const payload = {
        title,
        description: description || '',
        startTime: new Date(startTime),
        endTime: new Date(endTime),
        meetLink: meetLink || '',
        createdAt: this.props.firebase.fieldValue?.serverTimestamp
          ? this.props.firebase.fieldValue.serverTimestamp()
          : new Date(),
      };
      await this.props.firebase.db.collection('academySessions').add(payload);
      this.setState({
        newSession: {
          title: '',
          description: '',
          startTime: '',
          endTime: '',
          meetLink: '',
        },
      });
    } catch (error) {
      console.error('Failed to create session:', error);
      alert('Failed to create session. Please try again.');
    }
  };

  deleteAcademySession = async (sessionId) => {
    if (!sessionId) return;
    const confirmDelete = window.confirm('Delete this training session?');
    if (!confirmDelete) return;
    try {
      await this.props.firebase.db.collection('academySessions').doc(sessionId).delete();
    } catch (error) {
      console.error('Failed to delete session:', error);
      alert('Failed to delete session. Please try again.');
    }
  };

  confirmMandateRegistration = async (registration) => {
    if (!registration?.id) return;
    try {
      const timestamp = this.props.firebase?.fieldValue?.serverTimestamp
        ? this.props.firebase.fieldValue.serverTimestamp()
        : new Date();
      await this.props.firebase.db
        .collection('exclusive_mandate_registrations')
        .doc(registration.id)
        .set(
          {
            status: 'confirmed',
            confirmedAt: timestamp,
            confirmedBy: this.props.firebase?.auth?.currentUser?.uid || null,
          },
          { merge: true },
        );
    } catch (error) {
      console.error('Failed to confirm registration:', error);
      alert('Failed to confirm registration. Please try again.');
    }
  };

  handleSignOut = () => {
    this.props.firebase.doSignOut();
    this.props.history.push(ROUTES.ADMIN_SIGNIN);
  };

  normalizeAssignedEmail = (value) => {
    const trimmed = String(value || '').trim();
    if (!trimmed) return '';
    if (trimmed.includes('@')) return trimmed;
    return `${trimmed}@douglasmccartney.com`;
  };

  handleAssignedEmailChange = (requestId, value) => {
    this.setState((prev) => ({
      assignedEmailEditMode: {
        ...prev.assignedEmailEditMode,
        [requestId]: true,
      },
      assignedEmailEdits: {
        ...prev.assignedEmailEdits,
        [requestId]: value,
      },
    }));
  };

  startAssignedEmailEdit = (request) => {
    const requestId = request.id;
    this.setState((prev) => ({
      assignedEmailEditMode: {
        ...prev.assignedEmailEditMode,
        [requestId]: true,
      },
      assignedEmailEdits: {
        ...prev.assignedEmailEdits,
        [requestId]:
          prev.assignedEmailEdits[requestId] ?? request.assignedEmail ?? '',
      },
    }));
  };

  saveAssignedEmail = async (request) => {
    const rawValue = this.state.assignedEmailEdits[request.id] ?? request.assignedEmail ?? '';
    const assignedEmail = this.normalizeAssignedEmail(rawValue);
    if (!assignedEmail) {
      alert('Please enter an assigned email.');
      return;
    }

    this.setState((prev) => ({
      savingAssignedEmail: { ...prev.savingAssignedEmail, [request.id]: true },
    }));

    try {
      const adminEmail = this.props.firebase.auth?.currentUser?.email || 'admin';
      const linkedEmail = request.userEmail || request.email || null;
      const payload = {
        assignedEmail,
        assignedEmailLinkedTo: linkedEmail,
        assignedAt: this.props.firebase.fieldValue.serverTimestamp(),
        assignedBy: adminEmail,
        updatedAt: this.props.firebase.fieldValue.serverTimestamp(),
      };

      await this.props.firebase.db
        .collection('onboardingRequests')
        .doc(request.id)
        .set(payload, { merge: true });

      this.setState((prev) => ({
        assignedEmailEdits: {
          ...prev.assignedEmailEdits,
          [request.id]: assignedEmail,
        },
        assignedEmailEditMode: {
          ...prev.assignedEmailEditMode,
          [request.id]: false,
        },
      }));
    } catch (error) {
      console.error('Error saving assigned email:', error);
      alert('Failed to save assigned email: ' + error.message);
    } finally {
      this.setState((prev) => ({
        savingAssignedEmail: { ...prev.savingAssignedEmail, [request.id]: false },
      }));
    }
  };

  handleCleanupIncomplete = async () => {
    const confirmCleanup = window.confirm(
      'This will delete ALL franchises with status "onboarding" (incomplete records). Are you sure?'
    );

    if (!confirmCleanup) return;

    try {
      const { franchises } = this.state;
      const incompleteFranchises = franchises.filter(f =>
        f.status === 'onboarding' || f.status === 'pending' && !f.personalDetails
      );

      if (incompleteFranchises.length === 0) {
        alert('No incomplete franchises found.');
        return;
      }

      let deleted = 0;
      for (const franchise of incompleteFranchises) {
        try {
          // Delete onboarding data if exists
          await this.props.firebase.db
            .collection('franchiseOnboarding')
            .doc(franchise.id)
            .delete()
            .catch(() => { }); // Ignore if doesn't exist

          // Delete franchise
          await this.props.firebase.db
            .collection('franchises')
            .doc(franchise.id)
            .delete();

          // Delete owner record if exists
          if (franchise.ownerId) {
            await this.props.firebase.franchiseAPI
              .franchiseOwner(franchise.ownerId)
              .delete()
              .catch(() => { }); // Ignore if doesn't exist
          }

          deleted++;
        } catch (error) {
          console.error(`Error deleting franchise ${franchise.id}:`, error);
        }
      }

      alert(`Successfully deleted ${deleted} incomplete franchise(s).`);
    } catch (error) {
      console.error('Error during cleanup:', error);
      alert('Error during cleanup: ' + error.message);
    }
  };

  getFilteredFranchises = () => {
    const { franchises, disapprovedFranchises, filter, searchQuery } = this.state;

    // Combine all franchises
    let allFranchises = [...franchises];

    // Add disapproved franchises if showing them
    if (filter === 'all' || filter === 'disapproved') {
      allFranchises = [...allFranchises, ...disapprovedFranchises];
    }

    // Apply status filter
    if (filter !== 'all') {
      allFranchises = allFranchises.filter(f => f.status === filter);
    }

    // Apply search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      allFranchises = allFranchises.filter(f =>
        (f.personalDetails?.fullName || '').toLowerCase().includes(query) ||
        (f.personalDetails?.email || '').toLowerCase().includes(query) ||
        (f.ownerEmail || '').toLowerCase().includes(query)
      );
    }

    return allFranchises;
  };

  getStatusCounts = () => {
    const { franchises, disapprovedFranchises, agreements } = this.state;
    return {
      total: franchises.length + disapprovedFranchises.length,
      pending: franchises.filter(f => f.status === 'pending').length,
      pending_setup: franchises.filter(f => f.status === 'pending_setup').length,
      active: franchises.filter(f => f.status === 'active').length,
      disapproved: disapprovedFranchises.length,
      totalAgreements: agreements.length,
      pending_agreement: agreements.filter(a => a.status === 'pending_agreement').length,
      agreed: agreements.filter(a => a.status === 'agreed').length,
      payment_pending: agreements.filter(a => a.status === 'agreed' && !a.paymentCompleted).length,
    };
  };

  getFilteredAgreements = () => {
    const { agreements, filter, searchQuery } = this.state;

    let filtered = [...agreements];

    // Filter by status
    if (filter !== 'all') {
      if (filter === 'payment_pending') {
        filtered = filtered.filter(a => a.status === 'agreed' && !a.paymentCompleted);
      } else {
        filtered = filtered.filter(a => a.status === filter);
      }
    }

    // Filter by search query
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(a =>
        (a.formData?.fullName || '').toLowerCase().includes(query) ||
        (a.formData?.email || '').toLowerCase().includes(query) ||
        (a.formData?.phone || '').toLowerCase().includes(query) ||
        (a.formData?.reraNumber || '').toLowerCase().includes(query) ||
        (a.formData?.city || '').toLowerCase().includes(query) ||
        (a.formData?.state || '').toLowerCase().includes(query) ||
        (a.formData?.panNumber || '').toLowerCase().includes(query) ||
        (a.formData?.gstNumber || '').toLowerCase().includes(query)
      );
    }

    return filtered;
  };

  getStatusBadge = (status) => {
    const badges = {
      pending: { label: 'Pending', color: '#ffc107', bg: '#fff3cd' },
      pending_setup: { label: 'Pending Setup', color: '#fd7e14', bg: '#ffe5d0' },
      active: { label: 'Active', color: '#28a745', bg: '#d4edda' },
      disapproved: { label: 'Disapproved', color: '#dc3545', bg: '#f8d7da' },
      onboarding: { label: 'Onboarding', color: '#6c757d', bg: '#e9ecef' },
      pending_agreement: { label: 'Pending Signature', color: '#007bff', bg: '#cfe2ff' },
      agreed: { label: 'Signed', color: '#28a745', bg: '#d4edda' },
      payment_pending: { label: 'Payment Pending', color: '#ffc107', bg: '#fff3cd' },
    };
    const badge = badges[status] || { label: status, color: '#6c757d', bg: '#e9ecef' };
    return (
      <span
        className="status-badge"
        style={{
          background: badge.bg,
          color: badge.color,
          border: `1px solid ${badge.color}30`
        }}
      >
        {badge.label}
      </span>
    );
  };

  formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  getFranchiseeId = (agreement) => {
    const stored = String(agreement?.franchiseeId || '').trim();
    if (stored) {
      const upper = stored.toUpperCase();
      return upper.startsWith('DM-') ? upper : `DM-${upper}`;
    }
    const source = String(agreement?.userId || agreement?.id || '')
      .replace(/[^a-zA-Z0-9]/g, '')
      .slice(0, 10)
      .toUpperCase();
    return source ? `DM-${source}` : 'DM-UNKNOWN';
  };

  viewDetails = (franchiseId) => {
    this.props.history.push(`/jeethbuildcon/dashboard/${franchiseId}`);
  };

  viewAgreementDetails = (agreementId) => {
    window.open(`/franchise-agreement/${agreementId}`, '_blank');
  };

  openDeleteModal = (agreement) => {
    this.setState({
      deleteModalOpen: true,
      agreementToDelete: agreement,
    });
  };

  closeDeleteModal = () => {
    this.setState({
      deleteModalOpen: false,
      agreementToDelete: null,
    });
  };

  deleteAgreement = async () => {
    const { agreementToDelete } = this.state;

    if (!agreementToDelete) return;

    this.setState({ deletingAgreement: true });

    try {
      // Delete the agreement document
      await this.props.firebase.db
        .collection('franchiseAgreements')
        .doc(agreementToDelete.id)
        .delete();

      console.log('âœ… Agreement deleted:', agreementToDelete.id);

      // Note: Payment record in 'payments' collection is preserved
      // User can create new agreement and payment will be auto-linked

      // Close modal and reset state
      this.setState({
        deleteModalOpen: false,
        agreementToDelete: null,
        deletingAgreement: false,
      });

      alert('Agreement deleted successfully. Payment record has been preserved.');

    } catch (error) {
      console.error('Error deleting agreement:', error);
      alert('Failed to delete agreement: ' + error.message);
      this.setState({ deletingAgreement: false });
    }
  };

  viewCustomerDetails = (agreementId) => {
    // Open in new tab
    window.open(`/jeethbuildcon/customer/${agreementId}`, '_blank');
  };

  viewCustomerDetailsInline = (agreement) => {
    console.log('ðŸ” Looking for onboarding data for agreement:', {
      agreementId: agreement.id,
      userId: agreement.userId,
      franchiseId: agreement.franchiseId
    });

    console.log('ðŸ“š Available onboarding data:', this.state.onboardingData.map(d => ({
      id: d.id,
      userId: d.userId,
      franchiseId: d.franchiseId
    })));

    // Find onboarding data for this customer
    const onboarding = this.state.onboardingData.find(
      data => data.userId === agreement.userId || data.franchiseId === agreement.franchiseId || data.id === agreement.franchiseId
    );

    console.log('ðŸ“‹ Found onboarding:', onboarding ? 'YES' : 'NO', onboarding);

    this.setState({
      customerDetailsModal: true,
      selectedCustomer: { agreement, onboarding }
    });
  };

  closeCustomerDetailsModal = () => {
    this.setState({
      customerDetailsModal: false,
      selectedCustomer: null
    });
  };

  openFranchiseeCardPreview = (agreement) => {
    this.setState({
      cardPreviewOpen: true,
      cardPreviewAgreement: agreement,
      cardPreviewHovered: false,
    });
  };

  closeFranchiseeCardPreview = () => {
    this.setState({
      cardPreviewOpen: false,
      cardPreviewAgreement: null,
      cardPreviewHovered: false,
    });
  };

  handleApproveAgreement = async (agreement) => {
    if (!agreement?.id) return;

    const confirmApprove = window.confirm(
      `Approve ${agreement.formData?.fullName || agreement.userName || 'this user'} for onboarding?`,
    );
    if (!confirmApprove) return;

    const adminEmail = this.props.firebase.auth.currentUser?.email || 'admin';

    try {
      const updatePayload = {
        adminApprovalStatus: 'approved',
        adminApprovedAt: this.props.firebase.fieldValue.serverTimestamp(),
        adminApprovedBy: adminEmail,
      };

      if (!agreement.franchiseeId) {
        updatePayload.franchiseeId = this.getFranchiseeId(agreement);
      }

      await this.props.firebase.db
        .collection('franchiseAgreements')
        .doc(agreement.id)
        .update(updatePayload);
    } catch (error) {
      console.error('Error approving agreement:', error);
      alert('Failed to approve user: ' + error.message);
    }
  };

  // Admin power: Generate card for user
  generateCardForUser = async (agreementId) => {
    if (!window.confirm('âœ… Generate franchisee card for this user?')) return;

    try {
      const agreement = this.state.agreements.find((item) => item.id === agreementId);
      const updatePayload = {
        franchiseeCardGenerated: true,
      };

      if (!agreement?.franchiseeCardGeneratedAt) {
        updatePayload.franchiseeCardGeneratedAt = this.props.firebase.fieldValue.serverTimestamp();
      }

      if (agreement && !agreement.franchiseeId) {
        updatePayload.franchiseeId = this.getFranchiseeId(agreement);
      }

      await this.props.firebase.db
        .collection('franchiseAgreements')
        .doc(agreementId)
        .update(updatePayload);

      alert('âœ… Card marked as generated!');
    } catch (error) {
      console.error('Error generating card:', error);
      alert('âŒ Failed: ' + error.message);
    }
  };

  // Admin power: Reset card generation
  resetCardGeneration = async (agreementId) => {
    if (!window.confirm('âš ï¸ Reset card generation for this user? They can generate it again.')) return;

    try {
      await this.props.firebase.db
        .collection('franchiseAgreements')
        .doc(agreementId)
        .update({
          franchiseeCardGenerated: false,
          franchiseeCardGeneratedAt: null,
        });

      alert('âœ… Card status reset!');
    } catch (error) {
      console.error('Error resetting card:', error);
      alert('âŒ Failed: ' + error.message);
    }
  };

  // Admin power: Delete user completely
  deleteUserCompletely = async (agreement) => {
    const confirmText = `âš ï¸ PERMANENTLY DELETE USER?\n\nName: ${agreement.formData?.fullName}\nEmail: ${agreement.formData?.email}\n\nThis will delete:\n- Agreement\n- Payment records\n- Franchise owner record\n\nType DELETE to confirm:`;

    const userInput = prompt(confirmText);
    if (userInput !== 'DELETE') {
      alert('Deletion cancelled.');
      return;
    }

    try {
      // Delete agreement
      await this.props.firebase.db.collection('franchiseAgreements').doc(agreement.id).delete();

      // Delete franchise owner record
      if (agreement.userId) {
        await this.props.firebase.db.collection('franchiseOwners').doc(agreement.userId).delete();
      }

      // Note: Payment record is preserved for audit trail

      alert('âœ… User deleted successfully!\n\nNote: Payment record preserved for audit.');
    } catch (error) {
      console.error('Error deleting user:', error);
      alert('âŒ Failed: ' + error.message);
    }
  };

  // Admin power: Edit user details
  editUserDetails = async (agreement) => {
    const newName = prompt('Enter new full name:', agreement.formData?.fullName);
    if (!newName || newName === agreement.formData?.fullName) return;

    try {
      await this.props.firebase.db
        .collection('franchiseAgreements')
        .doc(agreement.id)
        .update({
          'formData.fullName': newName,
        });

      alert('âœ… Name updated successfully!');
    } catch (error) {
      console.error('Error updating user:', error);
      alert('âŒ Failed: ' + error.message);
    }
  };

  // Admin power: Export all data
  exportAllData = () => {
    try {
      const data = {
        agreements: this.state.agreements,
        paymentCompletedUsers: this.state.paymentCompletedUsers,
        franchises: this.state.franchises,
        exportedAt: new Date().toISOString(),
      };

      const dataStr = JSON.stringify(data, null, 2);
      const dataBlob = new Blob([dataStr], { type: 'application/json' });
      const url = URL.createObjectURL(dataBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `admin-data-export-${new Date().toISOString().split('T')[0]}.json`;
      link.click();
      URL.revokeObjectURL(url);

      alert('âœ… Data exported successfully!');
    } catch (error) {
      console.error('Error exporting data:', error);
      alert('âŒ Failed: ' + error.message);
    }
  };

  // Debug: Log all data
  debugData = () => {
    console.log('=== ðŸ› ADMIN PANEL DEBUG DATA ===');
    console.log('ðŸ“‹ Agreements:', this.state.agreements);
    console.log('ðŸ’³ Payment Completed Users:', this.state.paymentCompletedUsers);
    console.log('ðŸ¢ Franchises:', this.state.franchises);
    console.log('==================================');
    alert('ðŸ› Debug data logged to console. Press F12 to view.');
  };

  render() {
    const {
      loading,
      searchQuery,
      currentPhase,
      paymentCompletedUsers,
      agreements,
      onboardingData,
      onboardingRequests,
      franchises,
      mandates,
      mandateRegistrations,
      mandatesLoading,
      academySessions,
      academySessionsLoading,
      newSession,
      academyEvents,
      academyEventsLoading,
      newEvent,
      supportTickets,
      supportLoading,
      supportError,
      supportSelectedId,
      supportMessages,
      supportMessagesLoading,
      supportResolving,
      supportStatusFilter,
      supportPriorityFilter,
      supportSortBy,
      supportSearchQuery,
      notificationsHealth,
      notificationCampaigns,
      notificationTemplates,
      notificationsLoading,
      notificationsError,
      notificationsSending,
      notificationsStatus,
      notificationsStatusType,
      notificationComposer,
    } = this.state;
    const filteredAgreements = this.getFilteredAgreements();
    const signedAgreements = this.state.agreements.filter(
      (agr) => agr.eStampSignedDocumentPath && agr.eStampSignedDocumentBucket,
    );
    const mandateItems = mandates.length > 0 ? mandates : mandatesSeedData;
    const selectedMandateId =
      this.state.selectedMandateId || (mandateItems[0] ? mandateItems[0].id : null);
    const selectedMandate = mandateItems.find((item) => item.id === selectedMandateId) || null;
    const registrationsByProject = (mandateRegistrations || []).reduce((acc, registration) => {
      const key = registration.projectId || 'unknown';
      if (!acc[key]) {
        acc[key] = [];
      }
      acc[key].push(registration);
      return acc;
    }, {});
    const formatDate = (value) => {
      if (!value) return '—';
      const date = value.toDate ? value.toDate() : new Date(value);
      if (Number.isNaN(date.getTime())) return '—';
      return date.toLocaleString();
    };
    const toTimestamp = (value) => {
      if (!value) return 0;
      if (typeof value?.toDate === 'function') {
        const date = value.toDate();
        return Number.isNaN(date?.getTime?.()) ? 0 : date.getTime();
      }
      if (typeof value?.seconds === 'number') {
        return value.seconds * 1000;
      }
      const parsed = new Date(value).getTime();
      return Number.isNaN(parsed) ? 0 : parsed;
    };
    const normalizeSupportPriority = (value) => {
      const raw = String(value || 'normal').trim().toLowerCase();
      if (raw === 'urgent' || raw === 'high' || raw === 'low' || raw === 'normal') return raw;
      return 'normal';
    };
    const formatSupportPriorityLabel = (value) => {
      const priority = normalizeSupportPriority(value);
      if (priority === 'urgent') return 'Urgent';
      if (priority === 'high') return 'High';
      if (priority === 'low') return 'Low';
      return 'Normal';
    };
    const supportTicketSearchTerm = String(supportSearchQuery || '').trim().toLowerCase();
    const sortedNotificationCampaigns = [...notificationCampaigns].sort(
      (a, b) => toTimestamp(b?.createdAt) - toTimestamp(a?.createdAt),
    );
    const notificationPreview = this.getNotificationPreviewModel();
    const notificationPreviewContentAlign =
      notificationPreview.design.contentAlign === 'center'
        ? 'center'
        : notificationPreview.design.contentAlign === 'end'
          ? 'flex-end'
          : 'flex-start';
    const notificationPreviewContentJustify =
      notificationPreview.design.contentAlign === 'center'
        ? 'center'
        : notificationPreview.design.contentAlign === 'end'
          ? 'flex-end'
          : 'flex-start';
    const agreementsByUserId = (this.state.agreements || []).reduce((acc, agreement) => {
      if (agreement.userId) {
        acc[agreement.userId] = agreement;
      }
      return acc;
    }, {});
    const onboardingRequestsByAssignedEmail = (this.state.onboardingRequests || []).reduce(
      (acc, request) => {
        if (request.assignedEmail) {
          acc[request.assignedEmail] = request;
        }
        return acc;
      },
      {},
    );
    const resolveRegistrationName = (registration) => {
      const linkedRequest = onboardingRequestsByAssignedEmail[registration.email] || null;
      const agreement = registration.registeredByUid
        ? agreementsByUserId[registration.registeredByUid]
        : null;
      return (
        linkedRequest?.fullName ||
        agreement?.formData?.fullName ||
        registration.name ||
        (registration.email ? registration.email.split('@')[0] : 'N/A')
      );
    };
    const resolveRegistrationPhone = (registration) => {
      const linkedRequest = onboardingRequestsByAssignedEmail[registration.email] || null;
      const agreement = registration.registeredByUid
        ? agreementsByUserId[registration.registeredByUid]
        : null;
      return (
        linkedRequest?.phone ||
        agreement?.formData?.phone ||
        agreement?.formData?.mobile ||
        registration.phone ||
        'N/A'
      );
    };
    const isRegistrationConfirmed = (registration) =>
      Boolean(registration.confirmedAt || registration.status === 'confirmed');
    const approvalQueue = [...this.state.agreements]
      .filter((agreement) => {
        const status = String(
          agreement.stampnsignStatus || agreement.eStampSignStatus || agreement.status || '',
        ).toLowerCase();
        const approvalStatus = String(
          agreement.adminApprovalStatus || agreement.approvalStatus || '',
        ).toLowerCase();
        const isApproved = approvalStatus === 'approved' || Boolean(agreement.adminApprovedAt);
        return (
          !isApproved &&
          (status === 'esigned' || status === 'estamp_esigned' || status === 'estamp_completed')
        );
      })
      .sort((a, b) => {
        const aTime =
          a.eStampSignUpdatedAt?.seconds ||
          a.eStampSignedAt?.seconds ||
          a.updatedAt?.seconds ||
          a.createdAt?.seconds ||
          0;
        const bTime =
          b.eStampSignUpdatedAt?.seconds ||
          b.eStampSignedAt?.seconds ||
          b.updatedAt?.seconds ||
          b.createdAt?.seconds ||
          0;
        return bTime - aTime;
      });
    const franchiseeCardAgreements = [...this.state.agreements]
      .filter((agreement) => {
        const approvalStatus = String(
          agreement.adminApprovalStatus || agreement.approvalStatus || '',
        ).toLowerCase();
        return approvalStatus === 'approved' || Boolean(agreement.adminApprovedAt);
      })
      .sort((a, b) => {
        const aTime =
          a.adminApprovedAt?.seconds ||
          a.updatedAt?.seconds ||
          a.createdAt?.seconds ||
          0;
        const bTime =
          b.adminApprovedAt?.seconds ||
          b.updatedAt?.seconds ||
          b.createdAt?.seconds ||
          0;
        return bTime - aTime;
      });
    const cardPreviewAgreement = this.state.cardPreviewAgreement;
    const cardTheme = {
      base: '#050505',
      surface: '#0c0c0c',
      border: 'rgba(255,255,255,0.14)',
      soft: 'rgba(255,255,255,0.08)',
      micro: 'rgba(255,255,255,0.55)',
      text: '#f8f8f8',
      muted: 'rgba(248,248,248,0.72)',
      faint: 'rgba(248,248,248,0.55)',
      shadow: '0 50px 120px rgba(0,0,0,0.85)',
      glow: 'rgba(255,255,255,0.06)',
      panel: 'rgba(255,255,255,0.02)',
      glass: 'rgba(255,255,255,0.06)',
    };
    const resolveCardDate = (value) => {
      if (!value) return null;
      if (value.toDate) return value.toDate();
      if (value.seconds) return new Date(value.seconds * 1000);
      const parsed = new Date(value);
      return Number.isNaN(parsed.getTime()) ? null : parsed;
    };
    const formatCardDate = (date) =>
      date
        ? date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }).toUpperCase()
        : null;
    const cardIssuedAt = resolveCardDate(cardPreviewAgreement?.franchiseeCardGeneratedAt);
    const cardIssuedDate = formatCardDate(cardIssuedAt);
    const cardValidThroughAt = cardIssuedAt ? new Date(cardIssuedAt.getTime()) : null;
    if (cardValidThroughAt) {
      cardValidThroughAt.setFullYear(cardValidThroughAt.getFullYear() + 5);
    }
    const cardValidThroughDate = formatCardDate(cardValidThroughAt);
    const cardValidityDisplay =
      cardIssuedDate && cardValidThroughDate ? (
        <>
          {cardIssuedDate}
          <br />
          <span style={{ fontSize: '10px', fontWeight: 500, letterSpacing: '1px', color: cardTheme.faint }}>
            to
          </span>
          <br />
          {cardValidThroughDate}
        </>
      ) : (
        'PENDING'
      );
    const franchiseeIdDisplay = cardPreviewAgreement ? this.getFranchiseeId(cardPreviewAgreement) : 'DM-UNKNOWN';

    if (loading) {
      return (
        <div className="admin-loading">
          <div className="spinner-large"></div>
          <p>Loading franchise data...</p>
        </div>
      );
    }

    return (
      <div className="admin-dashboard">
        {/* Header */}
        <header className="admin-header">
          <div className="admin-header-content">
            <div className="admin-branding">
              <h1>Platform Admin</h1>
              <p>Douglas & McCartney</p>
            </div>
            <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
              <button onClick={this.exportAllData} className="admin-cleanup-btn" style={{ background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)' }} title="Export all data as JSON">
                <span>ðŸ“¥ Export Data</span>
              </button>
              <button onClick={this.debugData} className="admin-cleanup-btn" style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }} title="Debug: Log all data to console">
                <span>ðŸ› Debug</span>
              </button>
              <button onClick={this.handleCleanupIncomplete} className="admin-cleanup-btn" title="Delete all incomplete franchise records">
                <span>ðŸ§¹ Cleanup</span>
              </button>
              <button onClick={this.handleSignOut} className="admin-signout-btn">
                <span>Sign Out</span>
                <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                </svg>
              </button>
            </div>
          </div>
        </header>

        {/* Main Content */}
        <main className="admin-main">
          <div className="admin-layout">
            {/* Left Sidebar */}
            <aside className="admin-sidebar">
              <nav className="admin-nav">
                <button
                  className={`nav-item ${currentPhase === 'approval' ? 'active' : ''}`}
                  onClick={() => this.setState({ currentPhase: 'approval', filter: 'all' })}
                >
                  <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span>Approval Phase</span>
                </button>
                <button
                  className={`nav-item ${currentPhase === 'agreement' ? 'active' : ''}`}
                  onClick={() => this.setState({ currentPhase: 'agreement', filter: 'all' })}
                >
                  <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-3-3v6M5 7a2 2 0 012-2h10a2 2 0 012 2v10a2 2 0 01-2 2H7a2 2 0 01-2-2V7z" />
                  </svg>
                  <span>Agreement Phase</span>
                </button>
                <button
                  className={`nav-item ${currentPhase === 'paymentSuccessful' ? 'active' : ''}`}
                  onClick={() => this.setState({ currentPhase: 'paymentSuccessful', filter: 'all' })}
                >
                  <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.1 0-2 .9-2 2v1H9a3 3 0 100 6h6a3 3 0 100-6h-1v-1c0-1.1-.9-2-2-2z" />
                  </svg>
                  <span>Payment Successful</span>
                </button>
                <button
                  className={`nav-item ${currentPhase === 'signedPdfs' ? 'active' : ''}`}
                  onClick={() => this.setState({ currentPhase: 'signedPdfs', filter: 'all' })}
                >
                  <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6M9 8h3m5.121-4H6.879A2 2 0 004.879 4L4 4.879A2 2 0 003 6.879V19a2 2 0 002 2h14a2 2 0 002-2V6.879a2 2 0 00-.586-1.414L19.121 4A2 2 0 0017.121 4z" />
                  </svg>
                  <span>Signed PDFs</span>
                </button>
                <button
                  className={`nav-item ${currentPhase === 'downloadedSignedPdfs' ? 'active' : ''}`}
                  onClick={() => this.setState({ currentPhase: 'downloadedSignedPdfs', filter: 'all' })}
                >
                  <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M9 19l3 3m0 0l3-3m-3 3V10" />
                  </svg>
                  <span>Downloaded Signed PDF</span>
                </button>
                <button
                  className={`nav-item ${currentPhase === 'customers' ? 'active' : ''}`}
                  onClick={() => this.setState({ currentPhase: 'customers', filter: 'all' })}
                >
                  <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                  </svg>
                  <span>Customer Details</span>
                </button>
                <button
                  className={`nav-item ${currentPhase === 'franchiseeCard' ? 'active' : ''}`}
                  onClick={() => this.setState({ currentPhase: 'franchiseeCard', filter: 'all' })}
                >
                  <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 7h16M4 7a2 2 0 012-2h12a2 2 0 012 2M4 7v10a2 2 0 002 2h12a2 2 0 002-2V7M6 11h4M6 15h8" />
                  </svg>
                  <span>Franchisee Card</span>
                </button>
                <button
                  className={`nav-item ${currentPhase === 'onboarding' ? 'active' : ''}`}
                  onClick={() => this.setState({ currentPhase: 'onboarding', filter: 'all' })}
                >
                  <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5h7a2 2 0 012 2v10a2 2 0 01-2 2H9m-4-4h8m-8-4h8m-8-4h3" />
                  </svg>
                  <span>Onboarding</span>
                </button>
                <button
                  className={`nav-item ${currentPhase === 'mandates' ? 'active' : ''}`}
                  onClick={() => this.setState({ currentPhase: 'mandates', filter: 'all' })}
                >
                  <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 5h14a2 2 0 012 2v11a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2zm3 4h8m-8 4h8m-8 4h4" />
                  </svg>
                  <span>Mandate</span>
                </button>
                <button
                  className={`nav-item ${currentPhase === 'training' ? 'active' : ''}`}
                  onClick={() => this.setState({ currentPhase: 'training', filter: 'all' })}
                >
                  <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6l7 3-7 3-7-3 7-3z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 10v6l7 3 7-3v-6" />
                  </svg>
                  <span>Training</span>
                </button>
                <button
                  className={`nav-item ${currentPhase === 'events' ? 'active' : ''}`}
                  onClick={() => this.setState({ currentPhase: 'events', filter: 'all' })}
                >
                  <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10m-12 8h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                  <span>Events</span>
                </button>
                <button
                  className={`nav-item ${currentPhase === 'notifications' ? 'active' : ''}`}
                  onClick={() => this.setState({ currentPhase: 'notifications', filter: 'all' })}
                >
                  <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V4a2 2 0 10-4 0v1.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0a3 3 0 11-6 0m6 0H9" />
                  </svg>
                  <span>Notifications</span>
                </button>
                <button
                  className={`nav-item ${currentPhase === 'support' ? 'active' : ''}`}
                  onClick={() => this.setState({ currentPhase: 'support', filter: 'all' })}
                >
                  <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 5.636l-1.414-1.414a2 2 0 00-2.828 0L7 11.343V15h3.657l7.707-7.707a2 2 0 000-2.828z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7l1 1" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 21h8" />
                  </svg>
                  <span>Support</span>
                </button>
              </nav>
            </aside>

            {/* Content Area */}
            <div className="admin-content-area">
              {currentPhase === 'approval' && (
                <div className="admin-panel-section">
                  <div className="panel-header">
                    <div>
                      <h3>Approval Phase</h3>
                      <p className="panel-sub">
                        Agreements that finished e-signing and await admin approval.
                      </p>
                    </div>
                    <div style={{ fontSize: '14px', color: '#6c757d' }}>
                      Pending approvals: {approvalQueue.length}
                    </div>
                  </div>

                  <div className="admin-table-card">
                    {approvalQueue.length === 0 ? (
                      <div className="empty-state">
                        <p>No approvals waiting yet.</p>
                      </div>
                    ) : (
                      <table className="admin-table">
                        <thead>
                          <tr>
                            <th>Name</th>
                            <th>Email</th>
                            <th>Phone</th>
                            <th>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {approvalQueue.map((agreement) => (
                            <tr key={agreement.id}>
                              <td>{agreement.formData?.fullName || agreement.userName || 'N/A'}</td>
                              <td>{agreement.formData?.email || agreement.userEmail || 'N/A'}</td>
                              <td>{agreement.formData?.phone || agreement.formData?.mobile || agreement.paymentContact || 'N/A'}</td>
                              <td>
                                <button
                                  className="action-btn view-btn"
                                  onClick={() => this.handleApproveAgreement(agreement)}
                                >
                                  Approve
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                  </div>
                </div>
              )}

              {currentPhase === 'agreement' && (
                <div className="admin-panel-section">
                  <div className="panel-header">
                    <div>
                      <h3>Agreement Phase</h3>
                      <p className="panel-sub">All generated agreements (live updates from users).</p>
                    </div>
                    <div className="search-box" style={{ maxWidth: '260px' }}>
                      <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                      </svg>
                      <input
                        type="text"
                        placeholder="Search by name or email..."
                        value={searchQuery}
                        onChange={(e) => this.setState({ searchQuery: e.target.value })}
                      />
                    </div>
                  </div>

                  <div className="admin-table-card">
                    {filteredAgreements.length === 0 ? (
                      <div className="empty-state">
                        <p>No agreements found.</p>
                      </div>
                    ) : (
                      <table className="admin-table">
                        <thead>
                          <tr>
                            <th>Name</th>
                            <th>Email</th>
                            <th>Agreement</th>
                          </tr>
                        </thead>
                        <tbody>
                          {filteredAgreements.map((agreement) => (
                            <tr key={agreement.id}>
                              <td>{agreement.formData?.fullName || 'N/A'}</td>
                              <td>{agreement.formData?.email || agreement.userEmail || 'N/A'}</td>
                              <td>
                                <button
                                  className="action-btn view-btn"
                                  onClick={() => this.viewAgreementDetails(agreement.id)}
                                >
                                  View Agreement
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                  </div>
                </div>
              )}

              {currentPhase === 'paymentSuccessful' && (
                <div className="admin-panel-section">
                  <div className="panel-header">
                    <div>
                      <h3>Payment Successful</h3>
                      <p className="panel-sub">Completed payments with details (invoices pending).</p>
                    </div>
                  </div>

                  <div className="admin-table-card">
                    {paymentCompletedUsers.length === 0 ? (
                      <div className="empty-state">
                        <p>No payments found.</p>
                      </div>
                    ) : (
                      <table className="admin-table">
                        <thead>
                          <tr>
                            <th>Name</th>
                            <th>Email</th>
                            <th>Payment ID</th>
                            <th>Amount</th>
                            <th>Method</th>
                            <th>Date</th>
                          </tr>
                        </thead>
                        <tbody>
                          {[...paymentCompletedUsers]
                            .sort((a, b) => (b.paymentCompletedAt?.seconds || 0) - (a.paymentCompletedAt?.seconds || 0))
                            .map((user) => (
                              <tr key={user.id}>
                                <td>{user.displayName || user.userName || 'N/A'}</td>
                                <td>{user.email || user.userEmail || 'N/A'}</td>
                                <td style={{ fontFamily: 'monospace', fontSize: '13px' }}>{user.paymentId || 'N/A'}</td>
                                <td>Rs. {(user.paymentAmount || 0).toLocaleString('en-IN')}</td>
                                <td>{user.paymentMethod || 'N/A'}</td>
                                <td>
                                  {user.paymentCompletedAt
                                    ? new Date(user.paymentCompletedAt.seconds * 1000).toLocaleString('en-IN')
                                    : 'N/A'}
                                </td>
                              </tr>
                            ))}
                        </tbody>
                      </table>
                    )}
                  </div>
                </div>
              )}

              {currentPhase === 'signedPdfs' && (
                <div className="admin-panel-section">
                  <div className="panel-header">
                    <div>
                      <h3>Signed PDFs (E-Stamp Provider)</h3>
                      <p className="panel-sub">Documents signed via e-stamp/e-sign provider (auto-downloaded)</p>
                    </div>
                  </div>

                  {(() => {
                    const estampSignedAgreements = signedAgreements.filter(
                      (agr) => agr.eStampSignedDocumentPath && agr.eStampSignedDocumentPath.includes('signed-estamps/')
                    );
                    return estampSignedAgreements.length === 0 ? (
                      <div className="empty-state">
                        <p>No e-stamp signed documents available yet.</p>
                      </div>
                    ) : (
                      <div className="agreement-grid">
                        {estampSignedAgreements.map((agreement) => {
                          const baseSignedUrl = `https://firebasestorage.googleapis.com/v0/b/${agreement.eStampSignedDocumentBucket}/o/${encodeURIComponent(
                            agreement.eStampSignedDocumentPath,
                          )}?alt=media`;
                          const signedUrl = agreement.eStampSignedDocumentDownloadUrl
                            ? agreement.eStampSignedDocumentDownloadUrl
                            : agreement.eStampSignedDocumentDownloadToken
                              ? `${baseSignedUrl}&token=${agreement.eStampSignedDocumentDownloadToken}`
                              : baseSignedUrl;
                          return (
                            <div key={agreement.id} className="agreement-card">
                              <div className="agreement-card-header">
                                <h3>{agreement.formData?.fullName || agreement.userEmail || agreement.id}</h3>
                                <span className="chip success">E-Stamped</span>
                              </div>
                              <p className="agreement-meta">
                                Txn ID: <span className="mono">{agreement.eStampSignTxnId || '—'}</span>
                              </p>
                              <p className="agreement-meta">
                                Email: {agreement.formData?.email || agreement.userEmail || '—'}
                              </p>
                              <div className="agreement-actions" style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                                <a
                                  href={`/franchise-agreement/${agreement.id}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="action-btn view-btn"
                                >
                                  View Agreement
                                </a>
                                <a
                                  href={signedUrl}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="action-btn delete-btn"
                                  style={{ background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)', border: 'none' }}
                                >
                                  View Signed PDF
                                </a>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    );
                  })()}
                </div>
              )}

              {currentPhase === 'downloadedSignedPdfs' && (
                <div className="admin-panel-section">
                  <div className="panel-header">
                    <div>
                      <h3>Downloaded Signed PDF</h3>
                      <p className="panel-sub">All signed documents (uploaded by users + auto-downloaded from provider)</p>
                    </div>
                  </div>

                  {(() => {
                    // Show ALL agreements that have a signed document (both user-uploaded and auto-downloaded)
                    const allSignedAgreements = this.state.agreements.filter(
                      (agr) => agr.eStampSignedDocumentPath && agr.eStampSignedDocumentBucket
                    );
                    return allSignedAgreements.length === 0 ? (
                      <div className="empty-state">
                        <p>No signed documents available yet.</p>
                      </div>
                    ) : (
                      <div className="agreement-grid">
                        {allSignedAgreements.map((agreement) => {
                          const baseSignedUrl = `https://firebasestorage.googleapis.com/v0/b/${agreement.eStampSignedDocumentBucket}/o/${encodeURIComponent(
                            agreement.eStampSignedDocumentPath,
                          )}?alt=media`;
                          const signedUrl = agreement.eStampSignedDocumentDownloadUrl
                            ? agreement.eStampSignedDocumentDownloadUrl
                            : agreement.eStampSignedDocumentDownloadToken
                              ? `${baseSignedUrl}&token=${agreement.eStampSignedDocumentDownloadToken}`
                              : baseSignedUrl;
                          return (
                            <div key={agreement.id} className="agreement-card" style={{ background: '#f8f9fa', border: '1px solid #e0e0e0' }}>
                              <div className="agreement-card-header">
                                <h3 style={{ color: '#1a1a1a' }}>{agreement.formData?.fullName || agreement.userEmail || agreement.id}</h3>
                                {agreement.eStampSignedDocumentPath?.includes('signed-estamps/') ? (
                                  <span className="chip success" style={{ background: '#d1fae5', color: '#065f46' }}>E-Stamped</span>
                                ) : (
                                  <span className="chip success" style={{ background: '#dbeafe', color: '#1d4ed8' }}>Uploaded</span>
                                )}
                              </div>
                              <p className="agreement-meta" style={{ color: '#333' }}>
                                Email: {agreement.formData?.email || agreement.userEmail || '—'}
                              </p>
                              <p className="agreement-meta" style={{ color: '#333' }}>
                                Phone: {agreement.formData?.phone || '—'}
                              </p>
                              {agreement.eStampSignedDocumentUploadedAt && (
                                <p className="agreement-meta" style={{ color: '#333' }}>
                                  Uploaded: {this.formatDate(agreement.eStampSignedDocumentUploadedAt)}
                                </p>
                              )}
                              <div className="agreement-actions" style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                                <a
                                  href={`/franchise-agreement/${agreement.id}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="action-btn view-btn"
                                >
                                  View Agreement
                                </a>
                                <a
                                  href={signedUrl}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="action-btn delete-btn"
                                  style={{ background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)', border: 'none' }}
                                >
                                  Download Signed PDF
                                </a>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    );
                  })()}
                </div>
              )}

              {currentPhase === 'customers' && (
                <>
                  {/* Customer Details Stats */}
                  <div className="stats-grid">
                    <div className="stat-card">
                      <div className="stat-icon" style={{ background: 'linear-gradient(135deg, #28a745 0%, #20c997 100%)' }}>
                        <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                        </svg>
                      </div>
                      <div className="stat-content">
                        <h3>{this.getFilteredAgreements().length}</h3>
                        <p>Total Customers</p>
                      </div>
                    </div>

                    <div className="stat-card">
                      <div className="stat-icon" style={{ background: '#e3f2fd' }}>
                        <svg fill="none" viewBox="0 0 24 24" stroke="currentColor" style={{ color: '#2196f3' }}>
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                        </svg>
                      </div>
                      <div className="stat-content">
                        <h3>{this.getFilteredAgreements().filter(a => a.formData?.entityType === 'individual').length}</h3>
                        <p>Individual</p>
                      </div>
                    </div>

                    <div className="stat-card">
                      <div className="stat-icon" style={{ background: '#fff3e0' }}>
                        <svg fill="none" viewBox="0 0 24 24" stroke="currentColor" style={{ color: '#ff9800' }}>
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                        </svg>
                      </div>
                      <div className="stat-content">
                        <h3>{this.getFilteredAgreements().filter(a => a.formData?.entityType && a.formData.entityType !== 'individual').length}</h3>
                        <p>Business Entity</p>
                      </div>
                    </div>

                    <div className="stat-card">
                      <div className="stat-icon" style={{ background: '#f3e5f5' }}>
                        <svg fill="none" viewBox="0 0 24 24" stroke="currentColor" style={{ color: '#9c27b0' }}>
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                      </div>
                      <div className="stat-content">
                        <h3>{this.getFilteredAgreements().filter(a => a.agreedToTerms).length}</h3>
                        <p>Signed Agreements</p>
                      </div>
                    </div>
                  </div>

                  {/* Search Box */}
                  <div className="admin-controls">
                    <div className="search-box">
                      <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                      </svg>
                      <input
                        type="text"
                        placeholder="Search by name, email, phone, or RERA..."
                        value={searchQuery}
                        onChange={(e) => this.setState({ searchQuery: e.target.value })}
                      />
                    </div>
                  </div>

                  {/* Customer Details Table */}
                  <div className="franchise-table-container">
                    {this.getFilteredAgreements().length === 0 ? (
                      <div className="empty-state">
                        <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                        </svg>
                        <h3>No customers found</h3>
                        <p>There are no customer records matching your criteria.</p>
                      </div>
                    ) : (
                      <table className="franchise-table">
                        <thead>
                          <tr>
                            <th>Full Name</th>
                            <th>Actions</th>
                            <th>Email</th>
                            <th>Phone</th>
                            <th>Entity Type</th>
                            <th>Card Status</th>
                            <th>Team Size</th>
                            <th>Territory</th>
                            <th>Onboarding Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          {this.getFilteredAgreements().map(agreement => {
                            // Find matching onboarding data for this agreement
                            const onboarding = this.state.onboardingData.find(
                              data => data.userId === agreement.userId || data.franchiseId === agreement.franchiseId || data.id === agreement.franchiseId
                            );

                            return (
                              <tr key={agreement.id}>
                                <td>
                                  <div className="owner-cell">
                                    <div className="owner-avatar">
                                      {(agreement.formData?.fullName || 'U')[0].toUpperCase()}
                                    </div>
                                    <span>{agreement.formData?.fullName || 'N/A'}</span>
                                  </div>
                                </td>
                                <td>
                                  <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                                    <button
                                      className="action-btn"
                                      onClick={() => this.viewCustomerDetailsInline(agreement)}
                                      style={{
                                        background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                                        color: 'white',
                                        border: 'none',
                                        borderRadius: '6px',
                                        fontSize: '13px',
                                        fontWeight: '600',
                                        padding: '8px 16px',
                                        cursor: 'pointer',
                                        transition: 'all 0.2s',
                                        boxShadow: '0 2px 8px rgba(16, 185, 129, 0.3)'
                                      }}
                                      onMouseOver={(e) => {
                                        e.target.style.opacity = '0.8';
                                        e.target.style.transform = 'translateY(-2px)';
                                      }}
                                      onMouseOut={(e) => {
                                        e.target.style.opacity = '1';
                                        e.target.style.transform = 'translateY(0)';
                                      }}
                                    >
                                      ðŸ‘¤ View Details
                                    </button>
                                    <button
                                      className="action-btn"
                                      onClick={() => this.editUserDetails(agreement)}
                                      style={{
                                        background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
                                        color: 'white',
                                        border: 'none',
                                        borderRadius: '6px',
                                        fontSize: '12px',
                                        fontWeight: '600',
                                        padding: '6px 12px',
                                        cursor: 'pointer'
                                      }}
                                    >
                                      âœï¸ Edit
                                    </button>
                                    <button
                                      className="action-btn"
                                      onClick={() => this.deleteUserCompletely(agreement)}
                                      style={{
                                        background: 'linear-gradient(135deg, #dc3545 0%, #c82333 100%)',
                                        color: 'white',
                                        border: 'none',
                                        borderRadius: '6px',
                                        fontSize: '12px',
                                        fontWeight: '600',
                                        padding: '6px 12px',
                                        cursor: 'pointer'
                                      }}
                                    >
                                      ðŸ—‘ï¸ Delete
                                    </button>
                                  </div>
                                </td>
                                <td>{agreement.formData?.email || 'N/A'}</td>
                                <td>{agreement.formData?.phone || 'N/A'}</td>
                                <td>
                                  <span style={{
                                    textTransform: 'capitalize',
                                    padding: '4px 12px',
                                    borderRadius: '12px',
                                    background: agreement.formData?.entityType === 'individual' ? '#e3f2fd' : '#fff3e0',
                                    color: agreement.formData?.entityType === 'individual' ? '#1976d2' : '#f57c00',
                                    fontSize: '12px',
                                    fontWeight: '600'
                                  }}>
                                    {agreement.formData?.entityType || 'N/A'}
                                  </span>
                                </td>
                                <td>
                                  {agreement.franchiseeCardGenerated ? (
                                    <div style={{
                                      display: 'flex',
                                      flexDirection: 'column',
                                      gap: '4px',
                                      alignItems: 'flex-start'
                                    }}>
                                      <span style={{
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        gap: '6px',
                                        padding: '6px 12px',
                                        background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                                        color: 'white',
                                        borderRadius: '6px',
                                        fontSize: '13px',
                                        fontWeight: '600',
                                        boxShadow: '0 2px 8px rgba(16, 185, 129, 0.3)'
                                      }}>
                                        <svg width="14" height="14" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                        </svg>
                                        Generated
                                      </span>
                                      {agreement.franchiseeCardGeneratedAt && (
                                        <span style={{
                                          fontSize: '11px',
                                          color: '#666',
                                          paddingLeft: '4px'
                                        }}>
                                          {this.formatDate(agreement.franchiseeCardGeneratedAt)}
                                        </span>
                                      )}
                                    </div>
                                  ) : (
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                      <span style={{
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        gap: '6px',
                                        padding: '6px 12px',
                                        background: 'rgba(156, 163, 175, 0.15)',
                                        color: '#6b7280',
                                        borderRadius: '6px',
                                        fontSize: '13px',
                                        fontWeight: '500',
                                        border: '1px solid rgba(156, 163, 175, 0.3)'
                                      }}>
                                        <svg width="14" height="14" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                        </svg>
                                        Not Generated
                                      </span>
                                      <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                                        <button
                                          onClick={() => this.generateCardForUser(agreement.id)}
                                          style={{
                                            padding: '4px 10px',
                                            background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                                            color: 'white',
                                            border: 'none',
                                            borderRadius: '4px',
                                            fontSize: '11px',
                                            fontWeight: '600',
                                            cursor: 'pointer',
                                            transition: 'all 0.2s'
                                          }}
                                          onMouseOver={(e) => e.target.style.opacity = '0.8'}
                                          onMouseOut={(e) => e.target.style.opacity = '1'}
                                        >
                                          ðŸŽ« Generate
                                        </button>
                                        <button
                                          onClick={() => this.viewCustomerDetails(agreement.id)}
                                          style={{
                                            padding: '4px 10px',
                                            background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                                            color: 'white',
                                            border: 'none',
                                            borderRadius: '4px',
                                            fontSize: '11px',
                                            fontWeight: '600',
                                            cursor: 'pointer',
                                            transition: 'all 0.2s'
                                          }}
                                          onMouseOver={(e) => e.target.style.opacity = '0.8'}
                                          onMouseOut={(e) => e.target.style.opacity = '1'}
                                        >
                                          ðŸ‘¤ Customer Details
                                        </button>
                                      </div>
                                    </div>
                                  )}
                                </td>
                                <td>
                                  {onboarding?.step1?.teamSize ? (
                                    <span style={{
                                      padding: '4px 12px',
                                      borderRadius: '12px',
                                      background: '#e8f5e9',
                                      color: '#2e7d32',
                                      fontSize: '12px',
                                      fontWeight: '600'
                                    }}>
                                      ðŸ‘¥ {onboarding.step1.teamSize} members
                                    </span>
                                  ) : (
                                    <span style={{ color: '#999', fontSize: '13px' }}>Not filled</span>
                                  )}
                                </td>
                                <td>
                                  {onboarding?.step1?.territory ? (
                                    <span style={{
                                      fontSize: '13px',
                                      color: '#333',
                                      fontWeight: '500'
                                    }}>
                                      ðŸ“ {onboarding.step1.territory}
                                    </span>
                                  ) : (
                                    <span style={{ color: '#999', fontSize: '13px' }}>Not filled</span>
                                  )}
                                </td>
                                <td>
                                  {onboarding ? (
                                    onboarding.completed ? (
                                      <span style={{
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        gap: '6px',
                                        padding: '6px 12px',
                                        background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                                        color: 'white',
                                        borderRadius: '6px',
                                        fontSize: '12px',
                                        fontWeight: '600',
                                        boxShadow: '0 2px 8px rgba(16, 185, 129, 0.3)'
                                      }}>
                                        <svg width="12" height="12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                        </svg>
                                        Completed
                                      </span>
                                    ) : (
                                      <span style={{
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        gap: '6px',
                                        padding: '6px 12px',
                                        background: '#fff3cd',
                                        color: '#856404',
                                        borderRadius: '6px',
                                        fontSize: '12px',
                                        fontWeight: '600',
                                        border: '1px solid #ffeaa7'
                                      }}>
                                        â³ Step {onboarding.currentStep || 1} of 5
                                      </span>
                                    )
                                  ) : (
                                    <span style={{
                                      padding: '6px 12px',
                                      background: '#f8f9fa',
                                      color: '#6c757d',
                                      borderRadius: '6px',
                                      fontSize: '12px',
                                      fontWeight: '500',
                                      border: '1px solid #dee2e6'
                                    }}>
                                      Not started
                                    </span>
                                  )}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    )}
                  </div>
                </>
              )}

              {currentPhase === 'franchiseeCard' && (
                <div className="admin-panel-section">
                  <div className="panel-header">
                    <div>
                      <h3>Franchisee Card</h3>
                      <p className="panel-sub">Approved franchisees ready for card review.</p>
                    </div>
                    <div style={{ fontSize: '14px', color: '#6c757d' }}>
                      Approved: {franchiseeCardAgreements.length}
                    </div>
                  </div>

                  <div className="admin-table-card">
                    {franchiseeCardAgreements.length === 0 ? (
                      <div className="empty-state">
                        <p>No approved franchisees yet.</p>
                      </div>
                    ) : (
                      <table className="admin-table">
                        <thead>
                          <tr>
                            <th>Name</th>
                            <th>Email</th>
                            <th>Phone</th>
                            <th>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {franchiseeCardAgreements.map((agreement) => (
                            <tr key={agreement.id}>
                              <td>{agreement.formData?.fullName || agreement.userName || 'N/A'}</td>
                              <td>{agreement.formData?.email || agreement.userEmail || 'N/A'}</td>
                              <td>{agreement.formData?.phone || agreement.formData?.mobile || agreement.paymentContact || 'N/A'}</td>
                              <td>
                                <button
                                  className="action-btn view-btn"
                                  onClick={() => this.openFranchiseeCardPreview(agreement)}
                                >
                                  View User ID Card
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                  </div>
                </div>
              )}

              {currentPhase === 'onboarding' && (
                <div className="admin-panel-section">
                  <div className="panel-header">
                    <div>
                      <h3>Onboarding</h3>
                      <p className="panel-sub">Preferred email requests submitted by franchisees.</p>
                    </div>
                    <div style={{ fontSize: '14px', color: '#6c757d' }}>
                      Requests: {onboardingRequests.length}
                    </div>
                  </div>

                  <div className="admin-table-card">
                    {onboardingRequests.length === 0 ? (
                      <div className="empty-state">
                        <p>No onboarding requests yet.</p>
                      </div>
                    ) : (
                      <table className="admin-table">
                        <thead>
                          <tr>
                            <th>Name</th>
                            <th>Email</th>
                            <th>Phone</th>
                            <th>Preferred Emails</th>
                            <th>Assigned Email</th>
                            <th>Submitted</th>
                            <th>Edit Window</th>
                          </tr>
                        </thead>
                        <tbody>
                          {onboardingRequests.map((request) => {
                            const submittedAt = this.formatDate(request.submittedAt);
                            const isEditingAssignedEmail = this.state.assignedEmailEditMode[request.id] ?? !request.assignedEmail;
                            let editWindow = 'N/A';
                            if (request.editableUntil) {
                              const editDate = request.editableUntil.toDate
                                ? request.editableUntil.toDate()
                                : new Date(request.editableUntil);
                              editWindow = editDate.toLocaleString('en-IN', {
                                day: 'numeric',
                                month: 'short',
                                year: 'numeric',
                                hour: 'numeric',
                                minute: '2-digit',
                              });
                            } else if (request.submittedAt) {
                              const base = request.submittedAt.toDate
                                ? request.submittedAt.toDate()
                                : new Date(request.submittedAt);
                              if (!Number.isNaN(base.getTime())) {
                                const expires = new Date(base.getTime() + 24 * 60 * 60 * 1000);
                                editWindow = expires.toLocaleString('en-IN', {
                                  day: 'numeric',
                                  month: 'short',
                                  year: 'numeric',
                                  hour: 'numeric',
                                  minute: '2-digit',
                                });
                              }
                            }

                            return (
                              <tr key={request.id}>
                                <td>{request.fullName || 'N/A'}</td>
                                <td>{request.userEmail || 'N/A'}</td>
                                <td>{request.phone || 'N/A'}</td>
                                <td>
                                  {(request.preferredEmails || [])
                                    .filter(Boolean)
                                    .join(', ') || 'N/A'}
                                </td>
                                <td>
                                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', minWidth: '220px' }}>
                                    <input
                                      type="text"
                                      value={
                                        this.state.assignedEmailEdits[request.id] ??
                                        request.assignedEmail ??
                                        ''
                                      }
                                      onChange={(event) =>
                                        this.handleAssignedEmailChange(request.id, event.target.value)
                                      }
                                      placeholder="assigned@douglasmccartney.com"
                                      disabled={!isEditingAssignedEmail}
                                      style={{
                                        padding: '8px 10px',
                                        borderRadius: '6px',
                                        border: '1px solid #e0e0e0',
                                        fontSize: '13px',
                                        background: !isEditingAssignedEmail ? '#f3f4f6' : '#ffffff',
                                      }}
                                    />
                                    <button
                                      type="button"
                                      className="action-btn view-btn"
                                      onClick={() =>
                                        isEditingAssignedEmail
                                          ? this.saveAssignedEmail(request)
                                          : this.startAssignedEmailEdit(request)
                                      }
                                      disabled={Boolean(this.state.savingAssignedEmail[request.id])}
                                      style={{ alignSelf: 'flex-start' }}
                                    >
                                      {this.state.savingAssignedEmail[request.id]
                                        ? 'Saving...'
                                        : isEditingAssignedEmail
                                          ? 'Save'
                                          : 'Edit'}
                                    </button>
                                  </div>
                                </td>
                                <td>{submittedAt}</td>
                                <td>{editWindow}</td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    )}
                  </div>
                </div>
              )}

              {currentPhase === 'mandates' && (
                <div className="admin-panel-section">
                  <div className="panel-header">
                    <div>
                      <h3>Mandates</h3>
                      <p className="panel-sub">
                        Exclusive mandate projects and their channel partner registrations.
                      </p>
                    </div>
                    <div style={{ fontSize: '14px', color: '#6c757d' }}>
                      Projects: {mandateItems.length}
                    </div>
                  </div>

                  {mandatesLoading ? (
                    <div className="admin-table-card">
                      <div className="empty-state">
                        <p>Loading mandates...</p>
                      </div>
                    </div>
                  ) : (
                    <div className="mandates-tabs">
                      <div className="mandates-tablist">
                        {mandateItems.map((mandate) => {
                          const count = (registrationsByProject[mandate.id] || []).length;
                          const isActive = mandate.id === selectedMandateId;
                          return (
                            <button
                              key={mandate.id}
                              type="button"
                              className={`mandates-tab ${isActive ? 'active' : ''}`}
                              onClick={() => this.setState({ selectedMandateId: mandate.id })}
                            >
                              <span>{mandate.name}</span>
                              <em>{count}</em>
                            </button>
                          );
                        })}
                      </div>

                      {selectedMandate ? (
                        <div className="mandate-card">
                          <div className="mandate-card-header">
                            <div>
                              <h4>{selectedMandate.name}</h4>
                              <p>{selectedMandate.subtitle}</p>
                            </div>
                            <span className="mandate-count">
                              {(registrationsByProject[selectedMandate.id] || []).length} Registration
                              {(registrationsByProject[selectedMandate.id] || []).length === 1 ? '' : 's'}
                            </span>
                          </div>

                          <div className="mandate-meta">
                            <div>
                              <span>Location</span>
                              <strong>{selectedMandate.location || 'N/A'}</strong>
                            </div>
                            <div>
                              <span>Plot Sizes</span>
                              <strong>{selectedMandate.plotSizes || 'N/A'}</strong>
                            </div>
                            <div>
                              <span>Approvals</span>
                              <strong>{(selectedMandate.approvals || []).join(', ') || 'N/A'}</strong>
                            </div>
                            <div>
                              <span>Commission</span>
                              <strong>{selectedMandate.commission || '5%'}</strong>
                            </div>
                          </div>

                          <div className="mandate-registrations">
                            <div className="mandate-registrations-header">
                              <h5>Channel Partner Registrations</h5>
                              <span>{(registrationsByProject[selectedMandate.id] || []).length} total</span>
                            </div>

                            {(registrationsByProject[selectedMandate.id] || []).length === 0 ? (
                              <div className="empty-state small">
                                <p>No registrations yet.</p>
                              </div>
                            ) : (
                              <table className="admin-table compact">
                                <thead>
                                  <tr>
                                    <th>Name</th>
                                    <th>Email</th>
                                    <th>Phone</th>
                                    <th>Registered</th>
                                    <th>Confirm</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {(registrationsByProject[selectedMandate.id] || []).map((registration) => (
                                    <tr key={registration.id}>
                                      <td>{resolveRegistrationName(registration)}</td>
                                      <td>{registration.email || 'N/A'}</td>
                                      <td>{resolveRegistrationPhone(registration)}</td>
                                      <td>{formatDate(registration.createdAt)}</td>
                                      <td>
                                        {isRegistrationConfirmed(registration) ? (
                                          <span style={{ fontSize: '12px', color: '#1a7f37', fontWeight: 600 }}>
                                            Confirmed
                                          </span>
                                        ) : (
                                          <button
                                            type="button"
                                            className="action-btn view-btn"
                                            onClick={() => this.confirmMandateRegistration(registration)}
                                          >
                                            Confirm
                                          </button>
                                        )}
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            )}
                          </div>
                        </div>
                      ) : (
                        <div className="admin-table-card">
                          <div className="empty-state">
                            <p>No mandate selected.</p>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              {currentPhase === 'training' && (
                <div className="admin-panel-section">
                  <div className="panel-header">
                    <div>
                      <h3>Training Sessions</h3>
                      <p className="panel-sub">Create and manage online training sessions.</p>
                    </div>
                  </div>

                  <div className="admin-table-card training-admin">
                    <div className="training-form">
                      <div>
                        <label>Title</label>
                        <input
                          type="text"
                          value={newSession.title}
                          onChange={(event) =>
                            this.handleSessionFieldChange('title', event.target.value)
                          }
                          placeholder="Online Training Session"
                        />
                      </div>
                      <div>
                        <label>Description</label>
                        <input
                          type="text"
                          value={newSession.description}
                          onChange={(event) =>
                            this.handleSessionFieldChange('description', event.target.value)
                          }
                          placeholder="Session details"
                        />
                      </div>
                      <div>
                        <label>Start Time</label>
                        <input
                          type="datetime-local"
                          value={newSession.startTime}
                          onChange={(event) =>
                            this.handleSessionFieldChange('startTime', event.target.value)
                          }
                        />
                      </div>
                      <div>
                        <label>End Time</label>
                        <input
                          type="datetime-local"
                          value={newSession.endTime}
                          onChange={(event) =>
                            this.handleSessionFieldChange('endTime', event.target.value)
                          }
                        />
                      </div>
                      <div>
                        <label>Meet Link</label>
                        <input
                          type="text"
                          value={newSession.meetLink}
                          onChange={(event) =>
                            this.handleSessionFieldChange('meetLink', event.target.value)
                          }
                          placeholder="https://meet.google.com/..."
                        />
                      </div>
                      <button type="button" onClick={this.createAcademySession}>
                        Create Session
                      </button>
                    </div>

                    <div className="training-list">
                      <h4>Upcoming Sessions</h4>
                      {academySessionsLoading ? (
                        <div className="empty-state">Loading sessions...</div>
                      ) : academySessions.length === 0 ? (
                        <div className="empty-state">No sessions yet.</div>
                      ) : (
                        <table className="admin-table compact">
                          <thead>
                            <tr>
                              <th>Title</th>
                              <th>Start</th>
                              <th>End</th>
                              <th>Meet Link</th>
                              <th>Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {academySessions.map((session) => (
                              <tr key={session.id}>
                                <td>{session.title}</td>
                                <td>{formatDate(session.startTime)}</td>
                                <td>{formatDate(session.endTime)}</td>
                                <td>
                                  {session.meetLink ? (
                                    <a href={session.meetLink} target="_blank" rel="noreferrer">
                                      Open
                                    </a>
                                  ) : (
                                    '—'
                                  )}
                                </td>
                                <td>
                                  <button
                                    type="button"
                                    className="action-btn view-btn"
                                    onClick={() => this.deleteAcademySession(session.id)}
                                  >
                                    Delete
                                  </button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {currentPhase === 'events' && (
                <div className="admin-panel-section">
                  <div className="panel-header">
                    <div>
                      <h3>Events</h3>
                      <p className="panel-sub">Create and manage franchise events.</p>
                    </div>
                  </div>

                  <div className="admin-table-card training-admin">
                    <div className="training-form">
                      <div>
                        <label>Title</label>
                        <input
                          type="text"
                          value={newEvent.title}
                          onChange={(event) =>
                            this.handleEventFieldChange('title', event.target.value)
                          }
                          placeholder="Franchise Meetup"
                        />
                      </div>
                      <div>
                        <label>Description</label>
                        <input
                          type="text"
                          value={newEvent.description}
                          onChange={(event) =>
                            this.handleEventFieldChange('description', event.target.value)
                          }
                          placeholder="Event details"
                        />
                      </div>
                      <div>
                        <label>Start Time</label>
                        <input
                          type="datetime-local"
                          value={newEvent.startTime}
                          onChange={(event) =>
                            this.handleEventFieldChange('startTime', event.target.value)
                          }
                        />
                      </div>
                      <div>
                        <label>End Time</label>
                        <input
                          type="datetime-local"
                          value={newEvent.endTime}
                          onChange={(event) =>
                            this.handleEventFieldChange('endTime', event.target.value)
                          }
                        />
                      </div>
                      <div>
                        <label>Location</label>
                        <input
                          type="text"
                          value={newEvent.location}
                          onChange={(event) =>
                            this.handleEventFieldChange('location', event.target.value)
                          }
                          placeholder="Venue or city"
                        />
                      </div>
                      <button type="button" onClick={this.createAcademyEvent}>
                        Create Event
                      </button>
                    </div>

                    <div className="training-list">
                      <h4>Upcoming Events</h4>
                      {academyEventsLoading ? (
                        <div className="empty-state">Loading events...</div>
                      ) : academyEvents.length === 0 ? (
                        <div className="empty-state">No events yet.</div>
                      ) : (
                        <table className="admin-table compact">
                          <thead>
                            <tr>
                              <th>Title</th>
                              <th>Start</th>
                              <th>End</th>
                              <th>Location</th>
                              <th>Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {academyEvents.map((event) => (
                              <tr key={event.id}>
                                <td>{event.title}</td>
                                <td>{formatDate(event.startTime)}</td>
                                <td>{formatDate(event.endTime)}</td>
                                <td>{event.location || '—'}</td>
                                <td>
                                  <button
                                    type="button"
                                    className="action-btn view-btn"
                                    onClick={() => this.deleteAcademyEvent(event.id)}
                                  >
                                    Delete
                                  </button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      )}
                    </div>
                  </div>
                </div>
              )}
              {currentPhase === 'notifications' && (
                <div className="admin-panel-section">
                  <div className="panel-header">
                    <div>
                      <h3>Notifications</h3>
                      <p className="panel-sub">
                        Create and send notification campaigns to franchise users.
                      </p>
                    </div>
                  </div>

                  <div className="admin-table-card training-admin">
                    <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap', marginBottom: '4px' }}>
                      <div>
                        <strong>Service Status:</strong>{' '}
                        {notificationsHealth?.ok ? 'Healthy' : 'Unavailable'}
                      </div>
                      <div>
                        <strong>Templates:</strong>{' '}
                        {Number.isFinite(notificationsHealth?.templates) ? notificationsHealth.templates : '—'}
                      </div>
                      <div>
                        <strong>Queue Backlog:</strong>{' '}
                        {Number.isFinite(notificationsHealth?.pendingDeliveries)
                          ? notificationsHealth.pendingDeliveries
                          : '—'}
                      </div>
                      <div>
                        <strong>Loaded Templates:</strong>{' '}
                        {Array.isArray(notificationTemplates) ? notificationTemplates.length : 0}
                      </div>
                    </div>

                    <div className="training-form">
                      <div>
                        <label>Category</label>
                        <select
                          value={notificationComposer.category}
                          onChange={(event) =>
                            this.handleNotificationComposerChange('category', event.target.value)
                          }
                        >
                          <option value="SYSTEM">SYSTEM</option>
                          <option value="BILLING">BILLING</option>
                          <option value="PRODUCT">PRODUCT</option>
                          <option value="OPERATIONAL">OPERATIONAL</option>
                          <option value="MARKETING">MARKETING</option>
                          <option value="SECURITY">SECURITY</option>
                        </select>
                      </div>

                      <div>
                        <label>Channel</label>
                        <select
                          value={notificationComposer.channel}
                          onChange={(event) =>
                            this.handleNotificationComposerChange('channel', event.target.value)
                          }
                        >
                          <option value="in_app">In App</option>
                          <option value="email">Email</option>
                          <option value="both">Both (In App + Email)</option>
                          <option value="sms">SMS</option>
                          <option value="whatsapp">WhatsApp</option>
                          <option value="push">Push</option>
                          <option value="webpush">Web Push</option>
                        </select>
                      </div>

                      <div>
                        <label>Priority</label>
                        <select
                          value={notificationComposer.priority}
                          onChange={(event) =>
                            this.handleNotificationComposerChange('priority', event.target.value)
                          }
                        >
                          <option value="low">Low</option>
                          <option value="normal">Normal</option>
                          <option value="high">High</option>
                          <option value="urgent">Urgent</option>
                        </select>
                      </div>

                      <div>
                        <label>Audience</label>
                        <select
                          value={notificationComposer.targetMode}
                          onChange={(event) =>
                            this.handleNotificationComposerChange('targetMode', event.target.value)
                          }
                        >
                          <option value="all">All Users</option>
                          <option value="current">Current Franchise</option>
                          <option value="userIds">Specific User IDs</option>
                          <option value="franchiseIds">Specific Franchise IDs</option>
                        </select>
                      </div>

                      {notificationComposer.targetMode === 'userIds' && (
                        <div style={{ gridColumn: '1 / -1' }}>
                          <label>User IDs (comma separated)</label>
                          <input
                            type="text"
                            value={notificationComposer.userIdsRaw}
                            onChange={(event) =>
                              this.handleNotificationComposerChange('userIdsRaw', event.target.value)
                            }
                            placeholder="uid1, uid2, uid3"
                          />
                        </div>
                      )}

                      {notificationComposer.targetMode === 'franchiseIds' && (
                        <div style={{ gridColumn: '1 / -1' }}>
                          <label>Franchise IDs (comma separated)</label>
                          <input
                            type="text"
                            value={notificationComposer.franchiseIdsRaw}
                            onChange={(event) =>
                              this.handleNotificationComposerChange('franchiseIdsRaw', event.target.value)
                            }
                            placeholder="franchiseA, franchiseB"
                          />
                        </div>
                      )}

                      <div style={{ gridColumn: '1 / -1' }}>
                        <label>Template (optional)</label>
                        <select
                          value={notificationComposer.templateName}
                          onChange={(event) =>
                            this.handleNotificationComposerChange('templateName', event.target.value)
                          }
                        >
                          <option value="">No template (manual title/body)</option>
                          {(notificationTemplates || []).map((template) => (
                            <option key={template.id || template.name} value={template.name || ''}>
                              {template.name || template.id}
                              {template.channel ? ` (${template.channel})` : ''}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div style={{ gridColumn: '1 / -1' }}>
                        <label>Title</label>
                        <input
                          type="text"
                          value={notificationComposer.title}
                          onChange={(event) =>
                            this.handleNotificationComposerChange('title', event.target.value)
                          }
                          placeholder="Campaign title"
                        />
                      </div>

                      <div style={{ gridColumn: '1 / -1' }}>
                        <label>Body</label>
                        <textarea
                          rows={4}
                          value={notificationComposer.body}
                          onChange={(event) =>
                            this.handleNotificationComposerChange('body', event.target.value)
                          }
                          placeholder="Write your notification message"
                        />
                      </div>

                      <div className="notification-design-title">
                        <span>Creative Design</span>
                        <button
                          type="button"
                          className="notification-secondary-btn"
                          onClick={this.resetNotificationCreativeFields}
                          disabled={notificationsSending}
                        >
                          Reset design
                        </button>
                      </div>

                      <div className="notification-design-grid">
                        <div>
                          <label>Preset</label>
                          <select
                            value={notificationComposer.preset}
                            onChange={(event) =>
                              this.handleNotificationComposerChange('preset', event.target.value)
                            }
                          >
                            <option value="classic">Classic</option>
                            <option value="hero">Hero</option>
                            <option value="spotlight">Spotlight</option>
                            <option value="minimal">Minimal</option>
                          </select>
                        </div>

                        <div>
                          <label>Tone</label>
                          <select
                            value={notificationComposer.tone}
                            onChange={(event) =>
                              this.handleNotificationComposerChange('tone', event.target.value)
                            }
                          >
                            <option value="neutral">Neutral</option>
                            <option value="info">Info</option>
                            <option value="success">Success</option>
                            <option value="warning">Warning</option>
                            <option value="critical">Critical</option>
                          </select>
                        </div>

                        <div>
                          <label>Button Style</label>
                          <select
                            value={notificationComposer.buttonStyle}
                            onChange={(event) =>
                              this.handleNotificationComposerChange('buttonStyle', event.target.value)
                            }
                          >
                            <option value="solid">Solid</option>
                            <option value="outline">Outline</option>
                            <option value="ghost">Ghost</option>
                          </select>
                        </div>

                        <div>
                          <label>Auto Dismiss (sec)</label>
                          <input
                            type="number"
                            min="3"
                            max="60"
                            step="1"
                            value={notificationComposer.autoDismissSec}
                            onChange={(event) =>
                              this.handleNotificationComposerChange('autoDismissSec', event.target.value)
                            }
                            placeholder="7"
                          />
                        </div>

                        <div>
                          <label>Text Align</label>
                          <select
                            value={notificationComposer.textAlign}
                            onChange={(event) =>
                              this.handleNotificationComposerChange('textAlign', event.target.value)
                            }
                          >
                            <option value="left">Left</option>
                            <option value="center">Center</option>
                            <option value="right">Right</option>
                          </select>
                        </div>

                        <div>
                          <label>Content Align</label>
                          <select
                            value={notificationComposer.contentAlign}
                            onChange={(event) =>
                              this.handleNotificationComposerChange('contentAlign', event.target.value)
                            }
                          >
                            <option value="start">Start</option>
                            <option value="center">Center</option>
                            <option value="end">End</option>
                          </select>
                        </div>

                        <div>
                          <label>Image Position</label>
                          <select
                            value={notificationComposer.imagePosition}
                            onChange={(event) =>
                              this.handleNotificationComposerChange('imagePosition', event.target.value)
                            }
                          >
                            <option value="left">Left</option>
                            <option value="right">Right</option>
                            <option value="top">Top</option>
                          </select>
                        </div>

                        <div>
                          <label>Image Fit</label>
                          <select
                            value={notificationComposer.imageFit}
                            onChange={(event) =>
                              this.handleNotificationComposerChange('imageFit', event.target.value)
                            }
                          >
                            <option value="cover">Cover</option>
                            <option value="contain">Contain</option>
                          </select>
                        </div>

                        <div style={{ gridColumn: '1 / -1' }}>
                          <label>Image URL</label>
                          <input
                            type="text"
                            value={notificationComposer.imageUrl}
                            onChange={(event) =>
                              this.handleNotificationComposerChange('imageUrl', event.target.value)
                            }
                            placeholder="https://... (public image URL)"
                          />
                          <div className="notification-inline-help">
                            Use a direct public image URL. Google Drive share links are supported.
                          </div>
                        </div>

                        <div>
                          <label>Eyebrow</label>
                          <input
                            type="text"
                            value={notificationComposer.eyebrow}
                            onChange={(event) =>
                              this.handleNotificationComposerChange('eyebrow', event.target.value)
                            }
                            placeholder="Important update"
                          />
                        </div>

                        <div>
                          <label>Badge</label>
                          <input
                            type="text"
                            value={notificationComposer.badge}
                            onChange={(event) =>
                              this.handleNotificationComposerChange('badge', event.target.value)
                            }
                            placeholder="New"
                          />
                        </div>

                        <div>
                          <label>Icon</label>
                          <input
                            type="text"
                            value={notificationComposer.icon}
                            onChange={(event) =>
                              this.handleNotificationComposerChange('icon', event.target.value)
                            }
                            placeholder="⭐"
                          />
                        </div>

                        <div style={{ gridColumn: '1 / -1' }}>
                          <label>Subtitle</label>
                          <input
                            type="text"
                            value={notificationComposer.subtitle}
                            onChange={(event) =>
                              this.handleNotificationComposerChange('subtitle', event.target.value)
                            }
                            placeholder="Quick supporting line under title"
                          />
                        </div>

                        <div style={{ gridColumn: '1 / -1' }}>
                          <label>Footer</label>
                          <input
                            type="text"
                            value={notificationComposer.footer}
                            onChange={(event) =>
                              this.handleNotificationComposerChange('footer', event.target.value)
                            }
                            placeholder="Optional footer note"
                          />
                        </div>

                        <div>
                          <label>Primary CTA Label</label>
                          <input
                            type="text"
                            value={notificationComposer.ctaLabel}
                            onChange={(event) =>
                              this.handleNotificationComposerChange('ctaLabel', event.target.value)
                            }
                            placeholder="Open"
                          />
                        </div>

                        <div>
                          <label>Primary CTA URL</label>
                          <input
                            type="text"
                            value={notificationComposer.ctaUrl}
                            onChange={(event) =>
                              this.handleNotificationComposerChange('ctaUrl', event.target.value)
                            }
                            placeholder="https://..."
                          />
                        </div>

                        <div>
                          <label>Secondary CTA Label</label>
                          <input
                            type="text"
                            value={notificationComposer.secondaryCtaLabel}
                            onChange={(event) =>
                              this.handleNotificationComposerChange('secondaryCtaLabel', event.target.value)
                            }
                            placeholder="Learn more"
                          />
                        </div>

                        <div>
                          <label>Secondary CTA URL</label>
                          <input
                            type="text"
                            value={notificationComposer.secondaryCtaUrl}
                            onChange={(event) =>
                              this.handleNotificationComposerChange('secondaryCtaUrl', event.target.value)
                            }
                            placeholder="https://..."
                          />
                        </div>

                        <div>
                          <label>Accent Color</label>
                          <div className="notification-color-input-row">
                            <input
                              type="color"
                              className="notification-color-input"
                              value={notificationComposer.accentColor || '#6366f1'}
                              onChange={(event) =>
                                this.handleNotificationComposerChange('accentColor', event.target.value)
                              }
                            />
                            <input
                              type="text"
                              value={notificationComposer.accentColor}
                              onChange={(event) =>
                                this.handleNotificationComposerChange('accentColor', event.target.value)
                              }
                              placeholder="#6366f1"
                            />
                          </div>
                        </div>

                        <div>
                          <label>Background Color</label>
                          <div className="notification-color-input-row">
                            <input
                              type="color"
                              className="notification-color-input"
                              value={notificationComposer.backgroundColor || '#ffffff'}
                              onChange={(event) =>
                                this.handleNotificationComposerChange('backgroundColor', event.target.value)
                              }
                            />
                            <input
                              type="text"
                              value={notificationComposer.backgroundColor}
                              onChange={(event) =>
                                this.handleNotificationComposerChange('backgroundColor', event.target.value)
                              }
                              placeholder="#ffffff"
                            />
                          </div>
                        </div>

                        <div>
                          <label>Title Color</label>
                          <div className="notification-color-input-row">
                            <input
                              type="color"
                              className="notification-color-input"
                              value={notificationComposer.textColor || '#0f172a'}
                              onChange={(event) =>
                                this.handleNotificationComposerChange('textColor', event.target.value)
                              }
                            />
                            <input
                              type="text"
                              value={notificationComposer.textColor}
                              onChange={(event) =>
                                this.handleNotificationComposerChange('textColor', event.target.value)
                              }
                              placeholder="#0f172a"
                            />
                          </div>
                        </div>

                        <div>
                          <label>Body Color</label>
                          <div className="notification-color-input-row">
                            <input
                              type="color"
                              className="notification-color-input"
                              value={notificationComposer.bodyColor || '#334155'}
                              onChange={(event) =>
                                this.handleNotificationComposerChange('bodyColor', event.target.value)
                              }
                            />
                            <input
                              type="text"
                              value={notificationComposer.bodyColor}
                              onChange={(event) =>
                                this.handleNotificationComposerChange('bodyColor', event.target.value)
                              }
                              placeholder="#334155"
                            />
                          </div>
                        </div>
                      </div>

                      <div
                        className={`notification-preview-card ${notificationPreview.design.preset} tone-${notificationPreview.design.tone} button-${notificationPreview.design.buttonStyle} image-${notificationPreview.design.imagePosition}`}
                        style={{
                          ...(notificationPreview.design.accentColor
                            ? { '--notification-preview-accent': notificationPreview.design.accentColor }
                            : {}),
                          ...(notificationPreview.design.backgroundColor
                            ? { '--notification-preview-bg': notificationPreview.design.backgroundColor }
                            : {}),
                          ...(notificationPreview.design.textColor
                            ? { '--notification-preview-text': notificationPreview.design.textColor }
                            : {}),
                          ...(notificationPreview.design.bodyColor
                            ? { '--notification-preview-body': notificationPreview.design.bodyColor }
                            : {}),
                          '--notification-preview-content-align': notificationPreviewContentAlign,
                          '--notification-preview-content-justify': notificationPreviewContentJustify,
                          textAlign: notificationPreview.design.textAlign || 'left',
                        }}
                      >
                        {notificationPreview.design.imageUrl &&
                          notificationPreview.design.imagePosition !== 'right' && (
                            <div className="notification-preview-media">
                              <img
                                src={notificationPreview.design.imageUrl}
                                alt=""
                                loading="lazy"
                                style={{ objectFit: notificationPreview.design.imageFit || 'cover' }}
                                referrerPolicy="no-referrer"
                                onError={(event) => {
                                  const media = event.currentTarget.closest('.notification-preview-media');
                                  if (media) media.classList.add('is-broken');
                                }}
                              />
                            </div>
                          )}

                        <div className="notification-preview-content">
                          {(notificationPreview.design.icon ||
                            notificationPreview.design.badge ||
                            notificationPreview.design.eyebrow) && (
                            <div className="notification-preview-meta-row">
                              {notificationPreview.design.icon && (
                                <span className="notification-icon-chip">
                                  {notificationPreview.design.icon}
                                </span>
                              )}
                              {notificationPreview.design.badge && (
                                <span className="notification-badge-chip">
                                  {notificationPreview.design.badge}
                                </span>
                              )}
                              {notificationPreview.design.eyebrow && (
                                <span className="notification-preview-eyebrow">
                                  {notificationPreview.design.eyebrow}
                                </span>
                              )}
                            </div>
                          )}

                          <h4>{notificationPreview.title}</h4>
                          {notificationPreview.design.subtitle && (
                            <div className="notification-preview-subtitle">
                              {notificationPreview.design.subtitle}
                            </div>
                          )}
                          <p>{notificationPreview.body}</p>

                          {(notificationPreview.design.ctaLabel ||
                            notificationPreview.design.secondaryCtaLabel) && (
                            <div className="notification-preview-cta-row">
                              {notificationPreview.design.ctaLabel && (
                                <span
                                  className={`notification-preview-cta ${
                                    notificationPreview.design.ctaUrl ? '' : 'disabled'
                                  }`}
                                >
                                  {notificationPreview.design.ctaLabel}
                                </span>
                              )}
                              {notificationPreview.design.secondaryCtaLabel && (
                                <span
                                  className={`notification-preview-cta secondary ${
                                    notificationPreview.design.secondaryCtaUrl ? '' : 'disabled'
                                  }`}
                                >
                                  {notificationPreview.design.secondaryCtaLabel}
                                </span>
                              )}
                            </div>
                          )}

                          {notificationPreview.design.footer && (
                            <div className="notification-preview-footer">
                              {notificationPreview.design.footer}
                            </div>
                          )}
                        </div>

                        {notificationPreview.design.imageUrl &&
                          notificationPreview.design.imagePosition === 'right' && (
                            <div className="notification-preview-media right">
                              <img
                                src={notificationPreview.design.imageUrl}
                                alt=""
                                loading="lazy"
                                style={{ objectFit: notificationPreview.design.imageFit || 'cover' }}
                                referrerPolicy="no-referrer"
                                onError={(event) => {
                                  const media = event.currentTarget.closest('.notification-preview-media');
                                  if (media) media.classList.add('is-broken');
                                }}
                              />
                            </div>
                          )}
                      </div>

                      <div style={{ gridColumn: '1 / -1' }}>
                        <label>Data JSON (optional)</label>
                        <textarea
                          rows={3}
                          value={notificationComposer.dataJson}
                          onChange={(event) =>
                            this.handleNotificationComposerChange('dataJson', event.target.value)
                          }
                          placeholder='{"ctaText":"Open","ctaUrl":"https://douglasmccartney.com"}'
                        />
                      </div>

                      <div>
                        <label>Schedule At (optional)</label>
                        <input
                          type="datetime-local"
                          value={notificationComposer.scheduleAt}
                          onChange={(event) =>
                            this.handleNotificationComposerChange('scheduleAt', event.target.value)
                          }
                        />
                      </div>

                      <div className="notification-actions">
                        <div className="notification-actions-buttons">
                          <button
                            type="button"
                            onClick={this.sendNotificationCampaign}
                            disabled={notificationsSending}
                          >
                            {notificationsSending ? 'Sending...' : 'Send Campaign'}
                          </button>
                          <button
                            type="button"
                            className="notification-secondary-btn"
                            onClick={this.loadAdminNotifications}
                            disabled={notificationsLoading || notificationsSending}
                          >
                            Refresh
                          </button>
                        </div>
                        {notificationsStatus ? (
                          <div className="notification-actions-status">
                            <span
                              className={`notification-status ${
                                notificationsStatusType === 'error' ? 'error' : 'success'
                              }`}
                            >
                              {notificationsStatus}
                            </span>
                          </div>
                        ) : null}
                      </div>
                    </div>
                  </div>

                  <div className="notification-history">
                    <div className="notification-history-header">
                      <div>
                        <h4>Recent Campaigns</h4>
                        <p className="notification-history-sub">
                          Latest notification sends from admin panel.
                        </p>
                      </div>
                    </div>
                    {notificationsLoading ? (
                      <p className="notification-history-empty">Loading notification campaigns...</p>
                    ) : notificationsError ? (
                      <p className="notification-history-error">{notificationsError}</p>
                    ) : sortedNotificationCampaigns.length === 0 ? (
                      <p className="notification-history-empty">No notification campaigns yet.</p>
                    ) : (
                      <div className="notification-history-list">
                        {sortedNotificationCampaigns.map((campaign) => (
                          <div
                            key={campaign.id || `${campaign.createdAt}-${campaign.title || ''}`}
                            className="notification-history-item"
                          >
                            <div className="notification-history-top">
                              <div>
                                <div className="notification-history-title">
                                  {campaign.title || 'Notification'}
                                </div>
                                <div className="notification-history-meta">
                                  <span>{campaign.category || '—'}</span>
                                  <span>{campaign.channel || '—'}</span>
                                  <span>{formatDate(campaign.createdAt)}</span>
                                </div>
                              </div>
                              <div className="notification-history-stats">
                                <span>Created: {campaign.results?.created ?? 0}</span>
                                <span>Skipped: {campaign.results?.skipped ?? 0}</span>
                              </div>
                            </div>
                            {campaign.body ? (
                              <div className="notification-history-body">{campaign.body}</div>
                            ) : null}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}
              {currentPhase === 'support' && (
                <div className="admin-panel-section">
                  {(() => {
                    const openCount = supportTickets.filter((t) => (t.status || 'open') === 'open').length;
                    const waitingCount = supportTickets.filter((t) => t.status === 'waiting').length;
                    const resolvedCount = supportTickets.filter((t) => t.status === 'resolved').length;
                    const getSupportSortTimestamp = (ticket, mode) => {
                      if (mode === 'latest_created') {
                        return toTimestamp(ticket?.createdAt) || toTimestamp(ticket?.updatedAt);
                      }
                      if (mode === 'oldest_activity') {
                        return toTimestamp(ticket?.updatedAt) || toTimestamp(ticket?.createdAt);
                      }
                      return toTimestamp(ticket?.updatedAt) || toTimestamp(ticket?.createdAt);
                    };
                    const sortedSupportTickets = [...supportTickets].sort((a, b) => {
                      if (supportSortBy === 'oldest_activity') {
                        return (
                          getSupportSortTimestamp(a, supportSortBy) -
                          getSupportSortTimestamp(b, supportSortBy)
                        );
                      }
                      return (
                        getSupportSortTimestamp(b, supportSortBy) -
                        getSupportSortTimestamp(a, supportSortBy)
                      );
                    });
                    const filteredSupportTickets = sortedSupportTickets.filter((ticket) => {
                      const status = String(ticket.status || 'open').toLowerCase();
                      const priority = normalizeSupportPriority(ticket.priority);
                      if (supportStatusFilter !== 'all' && status !== supportStatusFilter) return false;
                      if (supportPriorityFilter !== 'all' && priority !== supportPriorityFilter) return false;
                      if (!supportTicketSearchTerm) return true;

                      const haystack = [
                        ticket.subject,
                        ticket.ticketNumber,
                        ticket.name,
                        ticket.email,
                        ticket.phone,
                        ticket.uid,
                        ticket.status,
                        ticket.priority,
                      ]
                        .map((value) => String(value || '').toLowerCase())
                        .join(' ');

                      return haystack.includes(supportTicketSearchTerm);
                    });

                    return (
                      <div className="support-admin">
                        <div className="support-admin-header">
                          <div>
                            <h3>Support</h3>
                            <p className="panel-sub">
                              Review support requests, user details, and conversation history.
                            </p>
                          </div>
                          <div className="support-admin-metrics">
                            <div>
                              <span>Total</span>
                              <strong>{supportTickets.length}</strong>
                            </div>
                            <div>
                              <span>Open</span>
                              <strong>{openCount}</strong>
                            </div>
                            <div>
                              <span>Waiting</span>
                              <strong>{waitingCount}</strong>
                            </div>
                            <div>
                              <span>Resolved</span>
                              <strong>{resolvedCount}</strong>
                            </div>
                          </div>
                        </div>

                        <div className="support-admin-grid">
                          <div className="support-admin-list">
                            {supportError ? (
                              <div className="support-admin-error">
                                {supportError}
                              </div>
                            ) : null}
                            <div className="support-admin-toolbar">
                              <div className="support-admin-toolbar-row">
                                <input
                                  type="search"
                                  className="support-admin-filter-input"
                                  placeholder="Search ticket #, subject, user, email..."
                                  value={supportSearchQuery}
                                  onChange={(event) =>
                                    this.setState({ supportSearchQuery: event.target.value })
                                  }
                                />
                                <select
                                  className="support-admin-filter-select"
                                  value={supportStatusFilter}
                                  onChange={(event) =>
                                    this.setState({ supportStatusFilter: event.target.value })
                                  }
                                >
                                  <option value="all">All statuses</option>
                                  <option value="open">Open</option>
                                  <option value="waiting">Waiting</option>
                                  <option value="resolved">Resolved</option>
                                </select>
                                <select
                                  className="support-admin-filter-select"
                                  value={supportPriorityFilter}
                                  onChange={(event) =>
                                    this.setState({ supportPriorityFilter: event.target.value })
                                  }
                                >
                                  <option value="all">All priorities</option>
                                  <option value="urgent">Urgent</option>
                                  <option value="high">High</option>
                                  <option value="normal">Normal</option>
                                  <option value="low">Low</option>
                                </select>
                                <select
                                  className="support-admin-filter-select"
                                  value={supportSortBy}
                                  onChange={(event) =>
                                    this.setState({ supportSortBy: event.target.value })
                                  }
                                >
                                  <option value="latest_activity">Latest activity</option>
                                  <option value="latest_created">Latest raised</option>
                                  <option value="oldest_activity">Oldest activity</option>
                                </select>
                                <button
                                  type="button"
                                  className="support-admin-filter-reset"
                                  onClick={() =>
                                    this.setState({
                                      supportSearchQuery: '',
                                      supportStatusFilter: 'all',
                                      supportPriorityFilter: 'all',
                                      supportSortBy: 'latest_activity',
                                    })
                                  }
                                >
                                  Reset
                                </button>
                              </div>
                              <div className="support-admin-toolbar-meta">
                                <span>
                                  Showing {filteredSupportTickets.length} of {supportTickets.length} tickets
                                </span>
                                <span>Sorted by {supportSortBy.replace(/_/g, ' ')}</span>
                              </div>
                            </div>
                            {supportLoading ? (
                              <div className="empty-state">
                                <p>Loading support tickets...</p>
                              </div>
                            ) : filteredSupportTickets.length === 0 ? (
                              <div className="empty-state">
                                <p>
                                  {supportTickets.length === 0
                                    ? 'No support tickets yet.'
                                    : 'No tickets match the selected filters.'}
                                </p>
                              </div>
                            ) : (
                              <div className="support-ticket-list">
                                {filteredSupportTickets.map((ticket) => {
                                  const isActive = supportSelectedId === ticket.id;
                                  const status = ticket.status || 'open';
                                  const priority = normalizeSupportPriority(ticket.priority);
                                  const isResolved = status === 'resolved';
                                  const isResolving = supportResolving?.[ticket.id];
                                  const statusLabel =
                                    status === 'resolved'
                                      ? 'Resolved'
                                      : status === 'waiting'
                                        ? 'Waiting'
                                        : 'Open';
                                  const onboardingByUid =
                                    onboardingData?.find((item) =>
                                      item.userId === ticket.uid ||
                                      item.franchiseId === ticket.uid ||
                                      item.id === ticket.uid,
                                    ) || null;
                                  const agreementByUser =
                                    agreements?.find((item) =>
                                      item.userId === ticket.uid ||
                                      item.userEmail === ticket.email ||
                                      item.id === ticket.uid,
                                    ) || null;
                                  const requestByEmail =
                                    onboardingRequests?.find((item) =>
                                      item.email === ticket.email ||
                                      item.userId === ticket.uid ||
                                      item.uid === ticket.uid,
                                    ) || null;
                                  const franchiseByUser =
                                    franchises?.find((item) =>
                                      item.userId === ticket.uid ||
                                      item.email === ticket.email ||
                                      item.id === ticket.uid,
                                    ) || null;

                                  const displayName =
                                    ticket.name ||
                                    agreementByUser?.formData?.fullName ||
                                    onboardingByUid?.personalDetails?.fullName ||
                                    requestByEmail?.fullName ||
                                    ticket.email ||
                                    ticket.uid ||
                                    'User';
                                  const displayEmail =
                                    ticket.email && ticket.email !== displayName ? ticket.email : '';
                                  const displayPhone =
                                    ticket.phone ||
                                    ticket.metadata?.phone ||
                                    onboardingByUid?.personalDetails?.phone ||
                                    onboardingByUid?.phone ||
                                    agreementByUser?.formData?.phone ||
                                    agreementByUser?.formData?.mobile ||
                                    agreementByUser?.paymentContact ||
                                    requestByEmail?.phone ||
                                    requestByEmail?.mobile ||
                                    franchiseByUser?.personalDetails?.phone ||
                                    franchiseByUser?.phone ||
                                    '';
                                  const updatedAt =
                                    ticket.updatedAt?.toDate?.() ||
                                    (ticket.updatedAt?.seconds
                                      ? new Date(ticket.updatedAt.seconds * 1000)
                                      : ticket.updatedAt
                                        ? new Date(ticket.updatedAt)
                                        : null);
                                  return (
                                    <button
                                      key={ticket.id}
                                      type="button"
                                      className={`support-ticket-card ${isActive ? 'active' : ''}`}
                                      onClick={() => this.handleSelectSupportTicket(ticket.id)}
                                    >
                                      <div className="support-ticket-meta">
                                        <span className={`support-status-pill ${status}`}>
                                          {statusLabel}
                                        </span>
                                        <span className={`support-priority-pill ${priority}`}>
                                          {formatSupportPriorityLabel(priority)}
                                        </span>
                                        <span className="support-ticket-number">
                                          {ticket.ticketNumber || 'SUP'}
                                        </span>
                                        <span className="support-ticket-time">
                                          {updatedAt ? updatedAt.toLocaleString() : 'N/A'}
                                        </span>
                                      </div>
                                      <div className="support-ticket-title">
                                        {ticket.subject || 'Support request'}
                                      </div>
                                      <div className="support-ticket-user">
                                        <strong>{displayName}</strong>
                                        {displayEmail && <span>{displayEmail}</span>}
                                        {displayPhone && <span>{displayPhone}</span>}
                                      </div>
                                      <div className="support-ticket-actions">
                                        <button
                                          type="button"
                                          className={`support-resolve-btn ${isResolved ? 'resolved' : ''}`}
                                          onClick={(event) => {
                                            event.stopPropagation();
                                            if (isResolved) return;
                                            this.handleResolveSupportTicket(ticket);
                                          }}
                                          disabled={isResolved || isResolving}
                                        >
                                          {isResolved ? 'Resolved' : isResolving ? 'Resolving...' : 'Resolve'}
                                        </button>
                                      </div>
                                    </button>
                                  );
                                })}
                              </div>
                            )}
                          </div>

                          <div className="support-admin-detail">
                            {!supportSelectedId ? (
                              <div className="empty-state">
                                <p>Select a ticket to view the conversation.</p>
                              </div>
                            ) : supportMessagesLoading ? (
                              <div className="empty-state">
                                <p>Loading messages...</p>
                              </div>
                            ) : (
                              <>
                                <div className="support-detail-header">
                                  <h4>Conversation</h4>
                                  <span>
                                    {supportMessages.length} message{supportMessages.length === 1 ? '' : 's'}
                                  </span>
                                </div>
                                <div className="support-thread">
                                  {supportMessages.length === 0 ? (
                                    <p>No messages yet.</p>
                                  ) : (
                                    supportMessages.map((message) => {
                                      const createdAt =
                                        message.createdAt?.toDate?.() ||
                                        (message.createdAt?.seconds
                                          ? new Date(message.createdAt.seconds * 1000)
                                          : message.createdAt
                                            ? new Date(message.createdAt)
                                            : null);
                                      const isSupport = message.sender === 'support';
                                      return (
                                        <div key={message.id} className={`support-message ${isSupport ? 'support' : 'user'}`}>
                                          <div className="support-message-meta">
                                            <span>{isSupport ? 'Support' : 'User'}</span>
                                            <span>{createdAt ? createdAt.toLocaleString() : 'N/A'}</span>
                                          </div>
                                          <div className="support-message-body">{message.text || '—'}</div>
                                          {Array.isArray(message.attachments) && message.attachments.length > 0 && (
                                            <div className="support-message-attachments">
                                              {message.attachments.map((att, index) => (
                                                <a
                                                  key={`${message.id}-att-${index}`}
                                                  href={att.url}
                                                  target="_blank"
                                                  rel="noopener noreferrer"
                                                >
                                                  {att.name || 'Attachment'}
                                                </a>
                                              ))}
                                            </div>
                                          )}
                                        </div>
                                      );
                                    })
                                  )}
                                </div>
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })()}
                </div>
              )}
            </div>
          </div>
        </main>

        {/* Delete Agreement Modal */}
        {this.state.deleteModalOpen && this.state.agreementToDelete && (
          <div className="modal-overlay" onClick={this.closeDeleteModal}>
            <div className="modal-container" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2>âš ï¸ Delete Franchise Agreement</h2>
                <button className="modal-close" onClick={this.closeDeleteModal}>Ã—</button>
              </div>

              <div className="modal-content">
                <div style={{ marginBottom: '20px' }}>
                  <h3 style={{ margin: '0 0 8px 0', color: '#333' }}>Agreement Details:</h3>
                  <p style={{ margin: '4px 0', color: '#666' }}>
                    <strong>Name:</strong> {this.state.agreementToDelete.formData?.fullName || 'N/A'}
                  </p>
                  <p style={{ margin: '4px 0', color: '#666' }}>
                    <strong>Email:</strong> {this.state.agreementToDelete.formData?.email || 'N/A'}
                  </p>
                  <p style={{ margin: '4px 0', color: '#666' }}>
                    <strong>RERA:</strong> {this.state.agreementToDelete.formData?.reraNumber || 'N/A'}
                  </p>
                </div>

                {this.state.agreementToDelete.paymentCompleted && (
                  <div style={{
                    background: 'rgba(255, 193, 7, 0.1)',
                    border: '2px solid #ffc107',
                    borderRadius: '8px',
                    padding: '16px',
                    marginBottom: '20px'
                  }}>
                    <h3 style={{ margin: '0 0 8px 0', color: '#ff9800', fontSize: '16px' }}>
                      ðŸ’° Payment Received
                    </h3>
                    <p style={{ margin: '4px 0', fontSize: '14px', color: '#666' }}>
                      <strong>Amount:</strong> â‚¹{(this.state.agreementToDelete.paymentAmount || 0).toLocaleString('en-IN')}
                    </p>
                    <p style={{ margin: '4px 0', fontSize: '14px', color: '#666' }}>
                      <strong>Payment ID:</strong> {this.state.agreementToDelete.paymentId || 'N/A'}
                    </p>
                    <p style={{ margin: '12px 0 0 0', fontSize: '13px', color: '#ff9800', fontWeight: '500' }}>
                      âš ï¸ Payment record will be preserved in the system
                    </p>
                  </div>
                )}

                <div style={{
                  background: 'rgba(220, 53, 69, 0.1)',
                  border: '1px solid #dc3545',
                  borderRadius: '8px',
                  padding: '16px',
                  marginBottom: '20px'
                }}>
                  <h3 style={{ margin: '0 0 8px 0', color: '#dc3545', fontSize: '15px' }}>
                    What happens after deletion:
                  </h3>
                  <ul style={{ margin: '8px 0 0 20px', fontSize: '14px', color: '#666' }}>
                    <li>Agreement document will be removed</li>
                    {this.state.agreementToDelete.paymentCompleted && (
                      <>
                        <li style={{ color: '#28a745', fontWeight: '500' }}>
                          Payment record will be preserved
                        </li>
                        <li style={{ color: '#28a745', fontWeight: '500' }}>
                          User can reapply without paying again
                        </li>
                      </>
                    )}
                    {!this.state.agreementToDelete.paymentCompleted && (
                      <li>User can create a new agreement</li>
                    )}
                    <li>This action cannot be undone</li>
                  </ul>
                </div>

                <p style={{ fontSize: '14px', color: '#666', marginBottom: '20px' }}>
                  Are you sure you want to delete this agreement?
                </p>
              </div>

              <div className="modal-footer">
                <button
                  className="modal-btn modal-btn-cancel"
                  onClick={this.closeDeleteModal}
                  disabled={this.state.deletingAgreement}
                >
                  Cancel
                </button>
                <button
                  className="modal-btn modal-btn-delete"
                  onClick={this.deleteAgreement}
                  disabled={this.state.deletingAgreement}
                >
                  {this.state.deletingAgreement ? 'Deleting...' : 'Delete Agreement'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Customer Details Modal */}
        {this.state.customerDetailsModal && this.state.selectedCustomer && (
          <div
            className="modal-overlay"
            onClick={this.closeCustomerDetailsModal}
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'rgba(0, 0, 0, 0.75)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 10000,
              padding: '40px',
              overflow: 'auto'
            }}
          >
            <div
              className="modal-container"
              onClick={(e) => e.stopPropagation()}
              style={{
                background: 'white',
                borderRadius: '12px',
                maxWidth: '1200px',
                width: '100%',
                maxHeight: '90vh',
                overflow: 'auto',
                boxShadow: '0 25px 80px rgba(0,0,0,0.4)',
                animation: 'slideUp 0.3s ease'
              }}
            >
              {/* Modal Header */}
              <div style={{
                position: 'sticky',
                top: 0,
                background: 'linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%)',
                color: 'white',
                padding: '25px 30px',
                borderRadius: '12px 12px 0 0',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                zIndex: 1,
                boxShadow: '0 2px 10px rgba(0,0,0,0.1)'
              }}>
                <h2 style={{
                  margin: 0,
                  fontSize: '28px',
                  fontWeight: '700',
                  fontFamily: "'Cormorant Garamond', serif"
                }}>
                  ðŸ‘¤ Customer Details
                </h2>
                <button
                  onClick={this.closeCustomerDetailsModal}
                  style={{
                    background: 'rgba(255,255,255,0.1)',
                    border: '2px solid rgba(255,255,255,0.2)',
                    color: 'white',
                    width: '40px',
                    height: '40px',
                    borderRadius: '6px',
                    fontSize: '24px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    transition: 'all 0.2s'
                  }}
                  onMouseOver={(e) => {
                    e.target.style.background = 'rgba(255,255,255,0.2)';
                    e.target.style.transform = 'rotate(90deg)';
                  }}
                  onMouseOut={(e) => {
                    e.target.style.background = 'rgba(255,255,255,0.1)';
                    e.target.style.transform = 'rotate(0deg)';
                  }}
                >Ã—</button>
              </div>

              {/* Modal Content */}
              <div style={{ padding: '30px' }}>
                {(() => {
                  const { agreement, onboarding } = this.state.selectedCustomer;

                  return (
                    <>
                      {/* Basic Agreement Info */}
                      <div style={{
                        background: '#f8f9fa',
                        borderRadius: '8px',
                        padding: '25px',
                        marginBottom: '25px',
                        border: '1px solid #e0e0e0'
                      }}>
                        <h3 style={{
                          margin: '0 0 20px 0',
                          fontSize: '20px',
                          fontWeight: '600',
                          color: '#1a1a1a',
                          fontFamily: "'Cormorant Garamond', serif",
                          paddingBottom: '12px',
                          borderBottom: '2px solid #ddd'
                        }}>
                          ðŸ“‹ Basic Information
                        </h3>
                        <div style={{
                          display: 'grid',
                          gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
                          gap: '15px'
                        }}>
                          <div style={{ padding: '12px', background: 'white', borderRadius: '6px', border: '1px solid #e8e8e8' }}>
                            <label style={{ display: 'block', fontSize: '11px', fontWeight: '700', color: '#666', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '6px' }}>Full Name</label>
                            <p style={{ margin: 0, fontSize: '15px', fontWeight: '500', color: '#1a1a1a' }}>{agreement.formData?.fullName || 'N/A'}</p>
                          </div>
                          <div style={{ padding: '12px', background: 'white', borderRadius: '6px', border: '1px solid #e8e8e8' }}>
                            <label style={{ display: 'block', fontSize: '11px', fontWeight: '700', color: '#666', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '6px' }}>Email</label>
                            <p style={{ margin: 0, fontSize: '15px', fontWeight: '500', color: '#1a1a1a' }}>{agreement.formData?.email || 'N/A'}</p>
                          </div>
                          <div style={{ padding: '12px', background: 'white', borderRadius: '6px', border: '1px solid #e8e8e8' }}>
                            <label style={{ display: 'block', fontSize: '11px', fontWeight: '700', color: '#666', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '6px' }}>Phone</label>
                            <p style={{ margin: 0, fontSize: '15px', fontWeight: '500', color: '#1a1a1a' }}>{agreement.formData?.phone || 'N/A'}</p>
                          </div>
                          <div style={{ padding: '12px', background: 'white', borderRadius: '6px', border: '1px solid #e8e8e8' }}>
                            <label style={{ display: 'block', fontSize: '11px', fontWeight: '700', color: '#666', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '6px' }}>Entity Type</label>
                            <p style={{ margin: 0, fontSize: '15px', fontWeight: '500', color: '#1a1a1a', textTransform: 'capitalize' }}>{agreement.formData?.entityType || 'N/A'}</p>
                          </div>
                          <div style={{ padding: '12px', background: 'white', borderRadius: '6px', border: '1px solid #e8e8e8' }}>
                            <label style={{ display: 'block', fontSize: '11px', fontWeight: '700', color: '#666', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '6px' }}>RERA Number</label>
                            <p style={{ margin: 0, fontSize: '15px', fontWeight: '500', color: '#1a1a1a' }}>{agreement.formData?.reraNumber || 'N/A'}</p>
                          </div>
                          <div style={{ padding: '12px', background: 'white', borderRadius: '6px', border: '1px solid #e8e8e8' }}>
                            <label style={{ display: 'block', fontSize: '11px', fontWeight: '700', color: '#666', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '6px' }}>Location</label>
                            <p style={{ margin: 0, fontSize: '15px', fontWeight: '500', color: '#1a1a1a' }}>{agreement.formData?.city ? `${agreement.formData.city}, ${agreement.formData.state}` : 'N/A'}</p>
                          </div>
                        </div>
                      </div>

                      {/* Franchise Application Form Data */}
                      <div style={{
                        background: '#f8f9fa',
                        borderRadius: '8px',
                        padding: '25px',
                        marginBottom: '25px',
                        border: '1px solid #e0e0e0'
                      }}>
                        <h3 style={{
                          margin: '0 0 20px 0',
                          fontSize: '20px',
                          fontWeight: '600',
                          color: '#1a1a1a',
                          fontFamily: "'Cormorant Garamond', serif",
                          paddingBottom: '12px',
                          borderBottom: '2px solid #ddd'
                        }}>
                          ðŸ“ Franchise Application Details
                        </h3>
                        <div style={{
                          display: 'grid',
                          gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
                          gap: '15px'
                        }}>
                          {Object.entries(agreement.formData).map(([key, value]) => (
                            <div key={key} style={{ padding: '12px', background: 'white', borderRadius: '6px', border: '1px solid #e8e8e8' }}>
                              <label style={{ display: 'block', fontSize: '11px', fontWeight: '700', color: '#666', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '6px' }}>
                                {key.replace(/([A-Z])/g, ' $1').trim()}
                              </label>
                              <p style={{ margin: 0, fontSize: '15px', fontWeight: '500', color: '#1a1a1a', wordBreak: 'break-word' }}>
                                {typeof value === 'object' ? JSON.stringify(value) : (value || 'N/A')}
                              </p>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Payment Information */}
                      {agreement.paymentCompleted && (
                        <div style={{
                          background: 'linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%)',
                          borderRadius: '8px',
                          padding: '25px',
                          border: '2px solid #10b981'
                        }}>
                          <h3 style={{
                            margin: '0 0 20px 0',
                            fontSize: '20px',
                            fontWeight: '600',
                            color: '#065f46',
                            fontFamily: "'Cormorant Garamond', serif",
                            paddingBottom: '12px',
                            borderBottom: '2px solid #a7f3d0'
                          }}>
                            ðŸ’³ Payment Information
                          </h3>
                          <div style={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
                            gap: '15px'
                          }}>
                            <div style={{ padding: '12px', background: 'white', borderRadius: '6px', border: '1px solid #e8e8e8' }}>
                              <label style={{ display: 'block', fontSize: '11px', fontWeight: '700', color: '#666', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '6px' }}>Payment Status</label>
                              <p style={{ margin: 0, fontSize: '15px', fontWeight: '700', color: '#10b981' }}>âœ… Completed</p>
                            </div>
                            {agreement.paymentId && (
                              <div style={{ padding: '12px', background: 'white', borderRadius: '6px', border: '1px solid #e8e8e8' }}>
                                <label style={{ display: 'block', fontSize: '11px', fontWeight: '700', color: '#666', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '6px' }}>Payment ID</label>
                                <p style={{ margin: 0, fontSize: '13px', fontWeight: '500', color: '#1a1a1a', fontFamily: 'monospace' }}>{agreement.paymentId}</p>
                              </div>
                            )}
                            {agreement.paymentCompletedAt && (
                              <div style={{ padding: '12px', background: 'white', borderRadius: '6px', border: '1px solid #e8e8e8' }}>
                                <label style={{ display: 'block', fontSize: '11px', fontWeight: '700', color: '#666', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '6px' }}>Payment Date</label>
                                <p style={{ margin: 0, fontSize: '15px', fontWeight: '500', color: '#1a1a1a' }}>
                                  {new Date(agreement.paymentCompletedAt.seconds * 1000).toLocaleString()}
                                </p>
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </>
                  );
                })()}
              </div>
            </div>
          </div>
        )}

        {this.state.cardPreviewOpen && cardPreviewAgreement && (
          <div className="modal-overlay" onClick={this.closeFranchiseeCardPreview}>
            <div
              className="modal-container"
              onClick={(e) => e.stopPropagation()}
              style={{
                maxWidth: '980px',
                width: '92%',
                background: '#0b0b0b',
                border: `1px solid ${cardTheme.border}`,
                color: cardTheme.text,
              }}
            >
              <div
                style={{
                  padding: '20px 24px',
                  borderBottom: `1px solid ${cardTheme.soft}`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                }}
              >
                <div>
                  <h2
                    style={{
                      margin: 0,
                      fontSize: '18px',
                      fontWeight: 600,
                      letterSpacing: '0.12em',
                      textTransform: 'uppercase',
                      fontFamily: 'var(--font-serif)',
                    }}
                  >
                    Franchisee Card
                  </h2>
                  <p style={{ margin: '6px 0 0', fontSize: '12px', color: cardTheme.muted }}>
                    {cardPreviewAgreement.formData?.fullName || cardPreviewAgreement.userName || 'Franchisee'}
                  </p>
                </div>
                <button
                  onClick={this.closeFranchiseeCardPreview}
                  style={{
                    background: 'transparent',
                    color: cardTheme.text,
                    border: `1px solid ${cardTheme.border}`,
                    width: '36px',
                    height: '36px',
                    cursor: 'pointer',
                    fontSize: '18px',
                    fontFamily: 'var(--font-sans)',
                  }}
                >
                  X
                </button>
              </div>

              <div style={{ padding: '28px', display: 'flex', justifyContent: 'center' }}>
                <div
                  style={{
                    width: 'min(560px, 92vw)',
                    aspectRatio: '28 / 17',
                    height: 'auto',
                    perspective: '2000px',
                  }}
                  onMouseEnter={() => this.setState({ cardPreviewHovered: true })}
                  onMouseLeave={() => this.setState({ cardPreviewHovered: false })}
                >
                  <div
                    style={{
                      width: '100%',
                      height: '100%',
                      position: 'relative',
                      transformStyle: 'preserve-3d',
                      transition: 'transform 1s cubic-bezier(0.23, 1, 0.32, 1)',
                      transform: this.state.cardPreviewHovered ? 'rotateY(180deg)' : 'rotateY(0deg)',
                    }}
                  >
                    <div
                      style={{
                        position: 'absolute',
                        inset: 0,
                        borderRadius: '26px',
                        padding: 'clamp(22px, 4vw, 36px) clamp(24px, 4.5vw, 42px)',
                        background: `linear-gradient(145deg, ${cardTheme.base} 0%, ${cardTheme.surface} 45%, ${cardTheme.base} 100%)`,
                        boxShadow: cardTheme.shadow,
                        border: `1px solid ${cardTheme.border}`,
                        overflow: 'hidden',
                        color: cardTheme.text,
                        fontFamily: "var(--font-sans)",
                        backfaceVisibility: 'hidden',
                      }}
                    >
                      <div
                        style={{
                          position: 'absolute',
                          inset: 0,
                          backgroundImage: `radial-gradient(circle at 20% 20%, ${cardTheme.glow}, transparent 52%), linear-gradient(115deg, ${cardTheme.soft} 0%, transparent 50%)`,
                          opacity: 0.85,
                          pointerEvents: 'none',
                        }}
                      ></div>
                      <div
                        style={{
                          position: 'absolute',
                          inset: 0,
                          backgroundImage: `linear-gradient(90deg, ${cardTheme.soft} 1px, transparent 1px), linear-gradient(180deg, ${cardTheme.soft} 1px, transparent 1px)`,
                          backgroundSize: '32px 32px',
                          opacity: 0.2,
                          pointerEvents: 'none',
                        }}
                      ></div>
                      <div
                        style={{
                          position: 'absolute',
                          top: '-40%',
                          right: '-10%',
                          width: '60%',
                          height: '200%',
                          background: `linear-gradient(120deg, ${cardTheme.soft}, transparent 60%)`,
                          opacity: 0.55,
                          transform: 'rotate(12deg)',
                        }}
                      ></div>
                      <div
                        style={{
                          position: 'absolute',
                          inset: '18px',
                          borderRadius: '20px',
                          border: `1px solid ${cardTheme.soft}`,
                          opacity: 0.45,
                          pointerEvents: 'none',
                        }}
                      ></div>

                      <div style={{ position: 'relative', zIndex: 2, height: '100%', display: 'flex', flexDirection: 'column', transform: 'translateY(-26px)' }}>
                        <div style={{ marginTop: 0 }}>
                          <p
                            style={{
                              margin: '10px 0 0 0',
                              fontSize: 'clamp(22px, 2.6vw, 30px)',
                              fontWeight: 500,
                              letterSpacing: '1.5px',
                              fontFamily: "var(--font-serif)",
                              textTransform: 'capitalize',
                              transform: 'translateY(-4px)',
                            }}
                          >
                            {cardPreviewAgreement.formData?.fullName || cardPreviewAgreement.userName || 'Franchisee Name'}
                          </p>
                          <p style={{ margin: '6px 0 0 0', letterSpacing: '4px', fontSize: '10px', color: cardTheme.faint, textTransform: 'uppercase', transform: 'translateY(-4px)' }}>
                            Exclusive Partner - Douglas & McCartney
                          </p>
                        </div>

                        <div style={{ marginTop: 'auto', display: 'flex', flexDirection: 'column', gap: '16px' }}>
                          <div
                            style={{
                              display: 'grid',
                              gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
                              gap: '12px',
                            }}
                          >
                            {[
                              { label: 'FRANCHISEE ID', value: franchiseeIdDisplay },
                              { label: 'TERRITORY', value: cardPreviewAgreement.formData?.city || 'India' },
                              { label: 'RERA NO.', value: cardPreviewAgreement.formData?.reraNumber || 'N/A' },
                              { label: 'VALIDITY', value: cardValidityDisplay },
                            ].map((item) => (
                              <div
                                key={item.label}
                                style={{
                                  border: `1px solid ${cardTheme.soft}`,
                                  padding: '10px 12px',
                                  borderRadius: '12px',
                                  background: cardTheme.panel,
                                }}
                              >
                                <p style={{ margin: 0, fontSize: '9px', letterSpacing: '2px', color: cardTheme.faint }}>{item.label}</p>
                                <p style={{ margin: '6px 0 0 0', fontSize: '12px', fontWeight: 600, lineHeight: 1.2, wordBreak: 'break-word' }}>{item.value}</p>
                              </div>
                            ))}
                          </div>

                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '14px', flexWrap: 'wrap' }}>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                              <p style={{ margin: 0, fontSize: '9px', letterSpacing: '2px', color: cardTheme.faint }}>CONTACT</p>
                              <p style={{ margin: 0, fontSize: '12px', color: cardTheme.muted }}>
                                {cardPreviewAgreement.formData?.email || cardPreviewAgreement.userEmail || 'N/A'}
                              </p>
                              <p style={{ margin: 0, fontSize: '12px', color: cardTheme.muted }}>
                                {cardPreviewAgreement.formData?.phone || cardPreviewAgreement.formData?.mobile || 'N/A'}
                              </p>
                            </div>
                          </div>

                          <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'flex-end', width: '100%' }}>
                            <div style={{ textAlign: 'right' }}>
                              <p style={{ margin: 0, fontSize: '9px', letterSpacing: '2px', color: cardTheme.faint }}>ISSUED BY</p>
                              <p style={{ margin: '4px 0 0 0', fontSize: '10px', letterSpacing: '2px', color: cardTheme.text }}>DOUGLAS & McCARTNEY BOARD</p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div
                      style={{
                        position: 'absolute',
                        inset: 0,
                        borderRadius: '26px',
                        background: `linear-gradient(145deg, ${cardTheme.base} 0%, ${cardTheme.surface} 50%, ${cardTheme.base} 100%)`,
                        border: `1px solid ${cardTheme.border}`,
                        backfaceVisibility: 'hidden',
                        transform: 'rotateY(180deg)',
                        overflow: 'hidden',
                        boxShadow: cardTheme.shadow,
                        color: cardTheme.text,
                      }}
                    >
                      <div style={{ position: 'absolute', inset: 0, backgroundImage: `radial-gradient(circle at 30% 30%, ${cardTheme.glow}, transparent 60%), radial-gradient(circle at 70% 70%, ${cardTheme.soft}, transparent 60%)`, opacity: 0.5 }}></div>
                      <div style={{ position: 'absolute', inset: '18px', borderRadius: '20px', border: `1px solid ${cardTheme.soft}`, opacity: 0.35 }}></div>
                      <div style={{ position: 'relative', zIndex: 2, height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <img
                          src="/Douglas2-removebg-preview.png"
                          alt="Douglas & McCartney"
                          style={{ width: '200px', height: '200px', objectFit: 'contain', filter: 'brightness(1.1)' }}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div style={{ padding: '0 28px 28px', color: cardTheme.muted, fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.16em' }}>
                {cardIssuedDate ? `Generated on ${cardIssuedDate}` : 'Card not generated yet'}
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }
}

const AdminDashboard = compose(
  withRouter,
  withFirebase,
  withAdminAuthorization,
)(AdminDashboardBase);

export default AdminDashboard;
